import { Injectable, OnDestroy, NgZone } from "@angular/core";
import {
  Session,
  SessionState,
  UserAgent,
  UserAgentOptions,
  InviterOptions,
  Inviter,
  SessionDescriptionHandler,
  Invitation,
  Registerer,
  URI,
  Web,
} from "sip.js";
import { BehaviorSubject, Observable, of } from "rxjs";
import { ToneService } from "../tone/tone.service";
import { AudioDeviceService } from "../audio-device/audio-device.service";
import { VideoDeviceService } from "../video/video-device.service";
import { VideoQualityService } from "../video/video-quality.service";
import {
  VideoCapability,
  VideoQuality,
  VideoStats,
} from "../video/video.interface";

interface SipConfig {
  authUser: string;
  authPassword: string;
  registrar: string;
  transportUrl: string;
  userAgent: string;
  displayName?: string;
  logging?: boolean;
  traceSip?: boolean;
  iceStunServerUrl?: string;
  iceTurnServerUrl?: string;
  iceTurnUsername?: string;
  iceTurnPassword?: string;
}

interface CustomSDH extends SessionDescriptionHandler {
  peerConnection: RTCPeerConnection;
}

export interface VideoUpgradeRequest {
  callId: string;
  remoteName: string;
  remoteNumber: string;
}

export interface CallInfo {
  session: Session;
  number: string;
  name?: string;
  state:
    | "idle"
    | "ringing"
    | "connected"
    | "held"
    | "transferring"
    | "conference"
    | "call-waiting"
    | "busy";
  isIncoming?: boolean;
  hasVideo: boolean;
  hasRemoteVideo: boolean;
  localStream: MediaStream | null;
  remoteStream: MediaStream | null;
  isMuted: boolean;
  isVideoMuted: boolean;
  videoCapabilities?: {
    local: boolean;
    remote: boolean;
  };
  videoQuality?: "auto" | "sd" | "hd" | "fhd";
  videoStats?: VideoStats;
  isConference?: boolean;
  participants?: string[];
  isCallWaiting?: boolean;
  /**
   * Indicates that the caller initiated dormant video in the outgoing call.
   * When true, video can be upgraded using the dormant video path (replaceTrack on existing sender).
   * This is only set for the caller (outgoing calls) - callee uses a different upgrade path.
   */
  hasCallerDormantVideo?: boolean;
  /**
   * Indicates that the user can upgrade to video (UI flag).
   * True when: dormant video was negotiated (either as caller or callee) and video is not active.
   */
  canUpgradeToVideo?: boolean;
}

export interface ActiveCallInfo extends CallInfo {
  id: string;
}

export interface IncomingCallCanceledEvent {
  callId: string;
  isCallWaiting?: boolean;
}

@Injectable({
  providedIn: "root",
})
export class SipService implements OnDestroy {
  private userAgent!: UserAgent;
  private registerer?: Registerer;
  private currentConfig?: SipConfig;
  private initialized = false;
  private _stopPromise: Promise<void> | null = null;
  private _pendingStopTimeout: ReturnType<typeof setTimeout> | null = null;
  private reconnectInterval: any;
  // flag to track stopping state of service
  private isStopping = false;

  // Registration status tracking
  private _isRegistered = new BehaviorSubject<boolean>(false);

  // call management
  private calls: Map<string, CallInfo> = new Map();
  private activeCalls = new BehaviorSubject<ActiveCallInfo[]>([]);
  private activeCallId = new BehaviorSubject<string | null>(null);
  private audioActiveCallId: string | null = null; // Tracks which call should have active audio
  private incomingInvitation: Invitation | null = null;

  // DND state
  private _isDoNotDisturbEnabled: boolean = false;

  // Flag to track if ringback tone is playing
  private isRingbackPlaying: boolean = false;

  // Track busy call auto-remove timeouts
  private busyCallTimeouts: Map<string, any> = new Map();

  // Track ongoing re-negotiations to prevent race conditions
  private ongoingRenegotiations = new Map<string, Promise<void>>();

  // Event for incoming call canceled - other services can subscribe to this
  private _incomingCallCanceled =
    new BehaviorSubject<IncomingCallCanceledEvent | null>(null);
  public incomingCallCanceled$ = this._incomingCallCanceled.asObservable();

  // Event for video upgrade requests - when remote party wants to add video to audio call
  private _videoUpgradeRequested =
    new BehaviorSubject<VideoUpgradeRequest | null>(null);
  public videoUpgradeRequested$ = this._videoUpgradeRequested.asObservable();
  private pendingVideoUpgradeCallId: string | null = null;

  // Connection attempt tracking
  private wsPreAuthAttempted = false;
  private wsConnectionAttempts = 0;
  private readonly MAX_CONNECTION_ATTEMPTS = 5;

  // Registration retry tracking
  private registrationRetryInterval: any;
  private registrationRetryAttempts = 0;
  private readonly MAX_REGISTRATION_RETRY_ATTEMPTS = 10;
  private isIntentionalUnregister = false;

  // Dormant video: Pre-acquired stream to inject into next call
  // This allows us to provide audio+dummy video without camera permission
  private pendingDormantVideoStream: MediaStream | null = null;

  // Stored reference to peerConnectionConfiguration so ICE servers can be updated at runtime
  private peerConnectionConfig: RTCConfiguration = {
    iceTransportPolicy: "all" as RTCIceTransportPolicy,
  };

  constructor(
    private toneService: ToneService,
    private audioDeviceService: AudioDeviceService,
    private videoDeviceService: VideoDeviceService,
    private videoQualityService: VideoQualityService,
    private ngZone: NgZone,
  ) {}

  // Getter for registration status
  get isRegistered(): boolean {
    return this._isRegistered.value;
  }

  // Observable for registration status
  getRegistrationStatus(): Observable<boolean> {
    return this._isRegistered.asObservable();
  }

  async initialize(sipConfig: SipConfig): Promise<void> {
    // Cancel any pending scheduled stop (e.g., from view switch widget destroy)
    this.cancelPendingStop();

    // Wait for any in-progress stop() to complete before initializing
    if (this._stopPromise) {
      await this._stopPromise;
    }

    // Skip if already initialized — SipService is a singleton that survives widget recreation
    if (this.initialized) {
      console.log(
        "[SipService] Already initialized, skipping re-initialization",
      );
      return;
    }

    // Reset registration status on initialization
    this._isRegistered.next(false);
    this.currentConfig = sipConfig;

    // Reset connection attempt tracking
    this.wsConnectionAttempts = 0;
    this.wsPreAuthAttempted = false;

    await this.setupUserAgent(sipConfig);
  }

  /**
   * Build ICE servers array from config and store on peerConnectionConfig.
   * Mutates the stored reference so existing UserAgent picks up changes for new sessions.
   */
  private buildIceServers(sipConfig: SipConfig): void {
    const iceServers: RTCIceServer[] = [];
    if (sipConfig.iceStunServerUrl) {
      iceServers.push({ urls: sipConfig.iceStunServerUrl });
    }
    if (sipConfig.iceTurnServerUrl) {
      const turnServer: RTCIceServer = { urls: sipConfig.iceTurnServerUrl };
      if (sipConfig.iceTurnUsername)
        turnServer.username = sipConfig.iceTurnUsername;
      if (sipConfig.iceTurnPassword)
        turnServer.credential = sipConfig.iceTurnPassword;
      iceServers.push(turnServer);
    }

    if (iceServers.length > 0) {
      this.peerConnectionConfig.iceServers = iceServers;
    } else {
      delete this.peerConnectionConfig.iceServers;
    }
    console.log("[SipService] ICE servers updated:", iceServers);
  }

  /**
   * Update ICE server configuration at runtime.
   * Takes effect for all new calls without requiring re-registration.
   */
  updateIceServers(iceConfig: {
    iceStunServerUrl?: string;
    iceTurnServerUrl?: string;
    iceTurnUsername?: string;
    iceTurnPassword?: string;
  }): void {
    if (this.currentConfig) {
      this.currentConfig.iceStunServerUrl = iceConfig.iceStunServerUrl;
      this.currentConfig.iceTurnServerUrl = iceConfig.iceTurnServerUrl;
      this.currentConfig.iceTurnUsername = iceConfig.iceTurnUsername;
      this.currentConfig.iceTurnPassword = iceConfig.iceTurnPassword;
    }
    this.buildIceServers(iceConfig as SipConfig);
  }

  /**
   * Sets the DND mode
   */
  setDoNotDisturb(enabled: boolean): void {
    this._isDoNotDisturbEnabled = enabled;
    console.log(`DND mode ${enabled ? "enabled" : "disabled"}`);
  }

  /**
   * Gets the DND state
   */
  get isDoNotDisturbEnabled(): boolean {
    return this._isDoNotDisturbEnabled;
  }

  /**
   * Check if there are any active calls (connected, held, transferring, or conference)
   */
  private hasActiveCalls(): boolean {
    return Array.from(this.calls.values()).some(
      (call) =>
        call.state === "connected" ||
        call.state === "held" ||
        call.state === "transferring" ||
        call.state === "conference",
    );
  }

  /**
   * Updates a call's audio input device
   */
  private async updateCallAudioInput(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) return;

    try {
      // Get new stream with selected device
      const newStream = await this.audioDeviceService.getAudioStream(
        call.hasVideo,
      );

      // Save mute state
      const wasMuted = call.isMuted;

      // Stop old tracks
      if (call.localStream) {
        call.localStream.getAudioTracks().forEach((track) => track.stop());
      }

      // Replace audio tracks
      const newAudioTrack = newStream.getAudioTracks()[0];

      // If there's an existing stream, replace just the audio track
      if (call.localStream) {
        // Remove old audio tracks
        const oldAudioTracks = call.localStream.getAudioTracks();
        oldAudioTracks.forEach((track) => {
          call.localStream?.removeTrack(track);
          track.stop();
        });

        // Add new audio track
        call.localStream.addTrack(newAudioTrack);
      } else {
        // If no existing stream, use the new one
        call.localStream = newStream;
      }

      // Apply mute state
      newAudioTrack.enabled = !wasMuted;
      call.isMuted = wasMuted;

      // Update the sender track in the RTCPeerConnection
      if (call.session.sessionDescriptionHandler) {
        const sdh = call.session.sessionDescriptionHandler as CustomSDH;
        if (sdh.peerConnection && sdh.peerConnection.getSenders) {
          const audioSender = sdh.peerConnection
            .getSenders()
            .find((sender) => sender.track && sender.track.kind === "audio");

          if (audioSender) {
            await audioSender.replaceTrack(newAudioTrack);
          }
        }
      }

      this.updateActiveCallsList();
    } catch (error) {
      console.error("Error updating call audio input:", error);
      throw error;
    }
  }

  /**
   * Updates a call's audio output device
   */
  private async updateCallAudioOutput(callId: string): Promise<void> {
    try {
      // Find all audio elements associated with this call and update them
      document
        .querySelectorAll(`audio[data-call-id="${callId}"]`)
        .forEach((audioElement) => {
          this.audioDeviceService.applyAudioOutputDevice(
            audioElement as HTMLAudioElement,
          );
        });
    } catch (error) {
      console.error("Error updating call audio output:", error);
      throw error;
    }
  }

  /**
   * Starts playing the ringback tone using tone service
   */
  private startRingbackTone(): void {
    if (this.isRingbackPlaying) {
      return; // Already playing
    }

    this.toneService.startRingbackTone();
    this.isRingbackPlaying = true;
    console.log("Started ringback tone");
  }

  /**
   * Stops the ringback tone using tone service
   */
  private stopRingbackTone(): void {
    if (!this.isRingbackPlaying) {
      return; // Already stopped
    }

    this.toneService.stopRingbackTone();
    this.isRingbackPlaying = false;
    console.log("Stopped ringback tone");
  }

  /**
   * Checks if the browser is Chromium-based (Chrome, Edge, Opera, Brave, etc.)
   */
  private isChromiumBrowser(): boolean {
    const userAgent = navigator.userAgent.toLowerCase();

    // Firefox detection
    if (userAgent.includes("firefox")) {
      return false;
    }

    // Safari detection
    if (userAgent.includes("safari") && !userAgent.includes("chrome")) {
      return false;
    }

    // Check for Chrome/Chromium signature
    // This will match Chrome, Edge, Opera, Brave, and other Chromium-based browsers
    return userAgent.includes("chrome") || userAgent.includes("chromium");
  }

  /**
   * Pre-authorizes a WebSocket connection by making an HTTPS request to the same domain
   * This helps overcome Chromium-based browsers' cross-origin WebSocket connection security model
   * @param wsUrl The WebSocket URL to pre-authorize
   * @returns A promise that resolves when pre-auth is complete
   */
  private async preAuthWebSocketConnection(wsUrl: string): Promise<void> {
    if (!this.isChromiumBrowser() || this.wsPreAuthAttempted) {
      return;
    }

    try {
      console.log("Attempting to pre-authorize WebSocket connection domain...");
      this.wsPreAuthAttempted = true;

      // Extract domain from WebSocket URL
      // Convert wss://domain/path to https://domain/
      const wsUrlObj = new URL(wsUrl);
      const domain = wsUrlObj.hostname;
      const httpsUrl = `https://${domain}/`;

      // Make a fetch request to the HTTPS endpoint
      // We don't care about the response, just that Chrome establishes trust with the domain
      const response = await fetch(httpsUrl, {
        method: "HEAD", // Use 'HEAD' to minimize data transfer
        mode: "no-cors", // Important: 'no-cors' allows the request without CORS headers
        cache: "no-cache",
        credentials: "omit",
        redirect: "follow",
        referrerPolicy: "no-referrer",
      });

      console.log("Successfully pre-authorized WebSocket domain");
    } catch (error) {
      console.warn(
        "Pre-authorization attempt failed, but proceeding anyway:",
        error,
      );
      // Even if this fails, we'll still attempt the WebSocket connection
    }
  }

  private createSipUri(number: string): URI | undefined {
    if (!this.currentConfig) return undefined;
    const uri = `sip:${number}@${this.currentConfig.registrar}`;
    return UserAgent.makeURI(uri);
  }

  private async setupUserAgent(sipConfig: SipConfig): Promise<void> {
    // Increment connection attempts counter
    this.wsConnectionAttempts++;

    // For Chromium browsers, pre-auth the WebSocket connection before attempting to connect
    if (
      this.isChromiumBrowser() &&
      sipConfig.transportUrl.startsWith("wss://") &&
      this.wsConnectionAttempts <= 2
    ) {
      await this.preAuthWebSocketConnection(sipConfig.transportUrl);
    }

    // Custom mediaStreamFactory that checks for pending dormant video stream
    // This allows us to inject audio+dummy video without camera permission
    const customMediaStreamFactory: Web.MediaStreamFactory = async (
      constraints: MediaStreamConstraints,
      sessionDescriptionHandler: Web.SessionDescriptionHandler,
    ): Promise<MediaStream> => {
      // If we have a pending dormant video stream, use it instead of calling getUserMedia
      if (this.pendingDormantVideoStream) {
        console.log("[mediaStreamFactory] Using pending dormant video stream");
        const stream = this.pendingDormantVideoStream;
        this.pendingDormantVideoStream = null; // Clear after use
        return stream;
      }

      // Default behavior: call getUserMedia with the provided constraints
      console.log(
        "[mediaStreamFactory] Using default getUserMedia with constraints:",
        constraints,
      );
      return navigator.mediaDevices.getUserMedia(constraints);
    };

    // Create custom session description handler factory with our media stream factory
    const customSessionDescriptionHandlerFactory =
      Web.defaultSessionDescriptionHandlerFactory(customMediaStreamFactory);

    // Build ICE servers from config into the stored peerConnectionConfig
    this.buildIceServers(sipConfig);

    const userAgentOptions: UserAgentOptions = {
      uri: this.createSipUri(sipConfig.authUser),
      authorizationPassword: sipConfig.authPassword,
      displayName: sipConfig.displayName || undefined, // Set display name if provided, undefined if empty
      viaHost: sipConfig.registrar,
      userAgentString: sipConfig.userAgent,
      logBuiltinEnabled: sipConfig.logging ?? true,
      noAnswerTimeout: 0,
      reconnectionAttempts: 0, // Disable SIP.js built-in reconnection; we handle it ourselves
      transportOptions: {
        server: sipConfig.transportUrl,
        traceSip: sipConfig.traceSip ?? true,
      },
      sessionDescriptionHandlerFactory: customSessionDescriptionHandlerFactory,
      sessionDescriptionHandlerFactoryOptions: {
        iceGatheringTimeout: 500,
        peerConnectionConfiguration: this.peerConnectionConfig,
      },
    };

    if (!userAgentOptions.uri) {
      throw new Error("Invalid SIP URI");
    }

    // Clean up old UserAgent to prevent ghost listeners and dangling WebSocket connections
    if (this.userAgent) {
      try {
        this.userAgent.transport.onConnect = () => {};
        this.userAgent.transport.onDisconnect = () => {};
        await this.userAgent.stop();
      } catch (e) {
        /* ignore cleanup errors */
      }
    }

    this.userAgent = new UserAgent(userAgentOptions);

    this.userAgent.delegate = {
      onInvite: (invitation: Invitation) => {
        this.handleIncomingCall(invitation);
      },
    };

    // setup transport event handlers
    this.userAgent.transport.onConnect = () => {
      console.log("WebSocket connected");
      this.clearReconnectInterval();
      this.registerUserAgent();
    };

    this.userAgent.transport.onDisconnect = (error?: Error) => {
      console.log("WebSocket disconnected", error);
      this.ngZone.run(() => this._isRegistered.next(false));

      // If this looks like a Chromium WebSocket cross-origin issue, we'll try once more
      // with pre-authorization on the next reconnect attempt
      if (
        error &&
        error.message &&
        error.message.includes("WebSocket closed") &&
        error.message.includes("(code: 1006)") &&
        this.isChromiumBrowser()
      ) {
        console.log(
          "Detected potential Chromium WebSocket cross-origin issue. Will retry with pre-authorization...",
        );
        // We'll let the reconnect interval handle the retry
      }

      this.startReconnectInterval();
    };

    try {
      await this.userAgent.start();
      this.initialized = true;
    } catch (error) {
      console.error("Error starting user agent:", error);
      this.ngZone.run(() => this._isRegistered.next(false));

      // If we suspect this is a Chromium WebSocket issue and we haven't tried too many times
      if (
        error instanceof Error &&
        error.message.includes("WebSocket closed") &&
        error.message.includes("(code: 1006)") &&
        this.isChromiumBrowser() &&
        this.wsConnectionAttempts <= this.MAX_CONNECTION_ATTEMPTS
      ) {
        // We'll retry with a longer delay via the reconnect interval
        console.log(
          `Chromium WebSocket connection issue detected (attempt ${this.wsConnectionAttempts}/${this.MAX_CONNECTION_ATTEMPTS}). Will retry...`,
        );
      }

      this.startReconnectInterval();
      throw error;
    }
  }

  private startReconnectInterval(): void {
    // don't start reconnect if service is stopping
    if (this.isStopping) {
      return;
    }

    if (!this.reconnectInterval && this.currentConfig) {
      console.log("Starting reconnection attempts...");

      // Use an exponential backoff strategy
      const baseDelay = 2000; // Start with 2 seconds
      const maxDelay = 30000; // Max 30 seconds between retries

      this.reconnectInterval = setInterval(async () => {
        try {
          // don't attempt reconnect if service is stopping
          if (this.isStopping) {
            this.clearReconnectInterval();
            return;
          }

          // If we've exceeded maximum connection attempts, use a longer interval
          // but still keep trying occasionally
          if (this.wsConnectionAttempts > this.MAX_CONNECTION_ATTEMPTS) {
            // Reduce frequency of retries after MAX_CONNECTION_ATTEMPTS
            clearInterval(this.reconnectInterval);
            this.reconnectInterval = setInterval(() => {
              if (!this.isStopping) {
                console.log("Periodic reconnection attempt...");
                this.setupUserAgent(this.currentConfig!).catch(() => {});
              } else {
                this.clearReconnectInterval();
              }
            }, 30000); // Try every 30 seconds
            return;
          }

          // Calculate delay for exponential backoff
          const delay = Math.min(
            baseDelay * Math.pow(1.5, this.wsConnectionAttempts - 1),
            maxDelay,
          );

          console.log(
            `Attempting to reconnect (attempt ${this.wsConnectionAttempts})...`,
          );
          await this.setupUserAgent(this.currentConfig!);
        } catch (error) {
          console.error("Reconnection attempt failed:", error);
        }
      }, 5000); // Initial delay of 5 seconds
    }
  }

  private clearReconnectInterval(): void {
    if (this.reconnectInterval) {
      console.log("Clearing reconnection interval");
      clearInterval(this.reconnectInterval);
      this.reconnectInterval = undefined;
    }
  }

  private startRegistrationRetry(): void {
    // Don't start retry if service is stopping
    if (this.isStopping) {
      return;
    }

    // Don't start a new retry interval if one is already running
    if (this.registrationRetryInterval) {
      console.log("Registration retry already in progress");
      return;
    }

    console.log("Starting registration retry mechanism...");

    // Calculate delay with exponential backoff
    const baseDelay = 2000; // Start with 2 seconds
    const maxDelay = 60000; // Max 60 seconds between retries
    const delay = Math.min(
      baseDelay * Math.pow(1.5, this.registrationRetryAttempts),
      maxDelay,
    );

    console.log(
      `Will retry registration in ${delay}ms (attempt ${this.registrationRetryAttempts + 1}/${this.MAX_REGISTRATION_RETRY_ATTEMPTS})`,
    );

    this.registrationRetryInterval = setTimeout(async () => {
      // Don't attempt retry if service is stopping
      if (this.isStopping) {
        this.clearRegistrationRetryInterval();
        return;
      }

      this.registrationRetryAttempts++;

      // If we've exceeded maximum attempts, keep trying but with longer intervals
      if (
        this.registrationRetryAttempts > this.MAX_REGISTRATION_RETRY_ATTEMPTS
      ) {
        console.log(
          "Maximum registration retry attempts exceeded, will retry periodically...",
        );
        this.clearRegistrationRetryInterval();

        // Continue retrying with a fixed long interval
        this.registrationRetryInterval = setInterval(async () => {
          if (
            !this.isStopping &&
            this.registerer &&
            !this._isRegistered.value
          ) {
            console.log("Periodic registration retry attempt...");
            try {
              await this.registerer.register();

              // Wait a bit for state to update, then check if we need to continue
              setTimeout(() => {
                if (this._isRegistered.value) {
                  this.clearRegistrationRetryInterval();
                }
              }, 500);
            } catch (error) {
              console.error("Periodic registration retry failed:", error);
            }
          } else if (this._isRegistered.value) {
            // Successfully registered - clear interval
            this.clearRegistrationRetryInterval();
          }
        }, 60000); // Try every 60 seconds
        return;
      }

      console.log(
        `Registration retry attempt ${this.registrationRetryAttempts}/${this.MAX_REGISTRATION_RETRY_ATTEMPTS}...`,
      );

      // Clear the timeout (we'll check state after attempt)
      this.clearRegistrationRetryInterval();

      try {
        if (this.registerer) {
          await this.registerer.register();
        } else {
          console.warn("Registerer not available, cannot retry registration");
        }

        // Wait a short time for state listener to update _isRegistered
        // If still not registered after 500ms, schedule next retry
        setTimeout(() => {
          if (!this._isRegistered.value && !this.isStopping) {
            console.log(
              "Registration attempt completed but still not registered, scheduling next retry...",
            );
            this.startRegistrationRetry();
          } else if (this._isRegistered.value) {
            console.log("Registration retry successful!");
          }
        }, 500);
      } catch (error) {
        console.error(
          `Registration retry attempt ${this.registrationRetryAttempts} failed with error:`,
          error,
        );

        // Schedule next retry on error
        if (!this.isStopping) {
          this.startRegistrationRetry();
        }
      }
    }, delay);
  }

  private clearRegistrationRetryInterval(): void {
    if (this.registrationRetryInterval) {
      console.log("Clearing registration retry interval");
      clearTimeout(this.registrationRetryInterval);
      clearInterval(this.registrationRetryInterval);
      this.registrationRetryInterval = undefined;
    }
  }

  private async registerUserAgent(): Promise<void> {
    if (!this.userAgent) {
      return;
    }

    try {
      console.log("Attempting to register...");

      // Clean up old Registerer to prevent ghost stateChange listeners
      if (this.registerer) {
        try {
          await this.registerer.dispose();
        } catch (e) {
          /* ignore cleanup errors */
        }
        this.registerer = undefined;
      }

      this.registerer = new Registerer(this.userAgent);

      // Add registration state change listener
      this.registerer.stateChange.addListener((state) => {
        const registered = state === "Registered";
        this.ngZone.run(() => this._isRegistered.next(registered));
        console.log(`Registration state changed to: ${state}`);

        // Handle registration state transitions
        if (state === "Registered") {
          // Registration succeeded - clear retry attempts and intervals
          this.registrationRetryAttempts = 0;
          this.clearRegistrationRetryInterval();
          console.log("Registration successful - cleared retry counters");
        } else if (
          state === "Unregistered" &&
          !this.isIntentionalUnregister &&
          !this.isStopping
        ) {
          // Only retry registration if transport is connected; otherwise reconnect handles it
          if (this.userAgent?.isConnected()) {
            console.log(
              "Unregistered state detected (not intentional) - starting registration retry mechanism",
            );
            this.startRegistrationRetry();
          } else {
            console.log(
              "Unregistered state detected but transport is disconnected - reconnect will handle re-registration",
            );
          }
        }
      });

      await this.registerer.register();
      console.log("Successfully registered");
      this.ngZone.run(() => this._isRegistered.next(true));
    } catch (error) {
      console.error("Registration failed:", error);
      this.ngZone.run(() => this._isRegistered.next(false));
      this.startRegistrationRetry();
    }
  }

  private setTracksEnabled(
    call: CallInfo,
    kind: "audio" | "video",
    enabled: boolean,
  ): void {
    // Special handling for audio tracks - don't disable audio for connected calls unless explicitly muted
    if (kind === "audio") {
      const callId = this.findCallIdBySession(call.session);
      const shouldKeepAudioEnabled =
        callId === this.audioActiveCallId && call.state === "connected";

      if (!enabled && shouldKeepAudioEnabled && !call.isMuted) {
        console.log(
          `Keeping audio enabled for connected call ${callId} (audio active call)`,
        );
        enabled = true;
      }
    }

    // update local stream tracks
    const tracks =
      call.localStream?.getTracks().filter((track) => track.kind === kind) ||
      [];
    tracks.forEach((track) => {
      track.enabled = enabled;
      console.log(`Local ${kind} track ${track.id} enabled: ${enabled}`);
    });

    // update RTCPeerConnection sender tracks if available
    if (call.session.sessionDescriptionHandler) {
      const sdh = call.session.sessionDescriptionHandler as CustomSDH;
      if (sdh.peerConnection && sdh.peerConnection.getSenders) {
        const senders = sdh.peerConnection.getSenders();
        senders.forEach((sender) => {
          if (sender.track && sender.track.kind === kind) {
            sender.track.enabled = enabled;
            console.log(
              `Sender ${kind} track ${sender.track.id} enabled: ${enabled}`,
            );
          }
        });
      }
    }
  }

  private setupSessionDelegates(session: Session): void {
    session.delegate = {
      onSessionDescriptionHandler: (handler: SessionDescriptionHandler) => {
        const sdh = handler as CustomSDH;
        const callId = this.findCallIdBySession(session);

        // Extract local stream from PeerConnection
        if (callId) {
          const call = this.calls.get(callId);
          if (call) {
            // Get local stream from peer connection senders
            const senders = sdh.peerConnection.getSenders();
            const tracks = senders
              .map((sender) => sender.track)
              .filter((track) => track !== null) as MediaStreamTrack[];
            if (tracks.length > 0) {
              call.localStream = new MediaStream(tracks);
              this.updateActiveCallsList();
            }
          }
        }

        // Listen for when local tracks are actually added to PeerConnection
        // (handles timing issues where SessionDescriptionHandler is created before tracks are added)
        sdh.peerConnection.addEventListener("negotiationneeded", () => {
          const callId = this.findCallIdBySession(session);
          if (callId) {
            const call = this.calls.get(callId);
            if (call && !call.localStream) {
              const senders = sdh.peerConnection.getSenders();
              const tracks = senders
                .map((sender) => sender.track)
                .filter((track) => track !== null) as MediaStreamTrack[];
              if (tracks.length > 0) {
                call.localStream = new MediaStream(tracks);
                this.updateActiveCallsList();
              }
            }
          }
        });

        sdh.peerConnection.ontrack = (event) => {
          console.log(
            "[SipService] Received remote track:",
            event.track.kind,
            "muted:",
            event.track.muted,
            "enabled:",
            event.track.enabled,
            "readyState:",
            event.track.readyState,
          );

          if (event.streams?.[0]) {
            const callId = this.findCallIdBySession(session);
            if (callId) {
              const call = this.calls.get(callId);
              if (call) {
                call.remoteStream = event.streams[0];

                // Check if remote stream has video
                const streamHasVideo =
                  event.streams[0].getVideoTracks().length > 0;

                // Don't set hasRemoteVideo=true if this is a video upgrade scenario
                // (call has no local video yet). This prevents the video UI from flashing
                // before the user accepts the upgrade. hasRemoteVideo will be set to true
                // in acceptVideoUpgrade() after user acceptance.
                if (streamHasVideo && !call.hasVideo) {
                  console.log(
                    "[SipService] Remote video track received but local has no video - likely video upgrade, deferring hasRemoteVideo",
                  );
                } else {
                  call.hasRemoteVideo = streamHasVideo;
                  console.log(
                    "[SipService] Remote stream has video:",
                    streamHasVideo,
                  );
                }

                // Start video stats monitoring if video is present
                if (call.hasVideo && call.hasRemoteVideo) {
                  this.startVideoStatsMonitoring(callId, sdh.peerConnection);
                }

                this.updateActiveCallsList();
              }
            }
          }
        };
      },

      // Handle re-INVITEs to capture updated caller ID information and video upgrades
      onInvite: (request) => {
        // This handles mid-dialog INVITEs (re-INVITEs)
        console.log("[SipService] Received re-INVITE within dialog");

        // Extract caller ID headers
        const remotePartyIdHeader = request.getHeader("Remote-Party-ID");
        const pAssertedIdHeader = request.getHeader("P-Asserted-Identity");

        // Update call with header information
        this.updateCallFromHeaders(
          session,
          remotePartyIdHeader,
          pAssertedIdHeader,
          "re-INVITE",
        );

        // Check if this is a video upgrade request
        const callId = this.findCallIdBySession(session);
        if (callId) {
          const call = this.calls.get(callId);
          // Detect video upgrade: call exists, we don't have local video, and remote is adding video
          // Note: hasRemoteVideo might already be true due to ontrack firing before onInvite
          if (call && !call.hasVideo && !this.pendingVideoUpgradeCallId) {
            // Check SDP for video m-line with sendrecv direction (not inactive)
            const sdp = request.body;
            const hasVideoInSDP = this.detectVideoInSDP(sdp);
            const isVideoActive = sdp && !sdp.includes("a=inactive");

            if (hasVideoInSDP && isVideoActive) {
              console.log(
                "[SipService] Detected video upgrade request from remote party",
              );

              // Note: SIP.js will auto-accept this re-INVITE with a 200 OK.
              // Even though we set the SDP modifier to sendrecv here, the dormant track
              // won't actually send video RTP. The actual video activation happens in
              // acceptVideoUpgrade() which:
              // 1. Replaces dormant track with real camera track
              // 2. Enables the video sender
              // 3. Sends a re-INVITE to trigger proper RTP transmission
              if (call.hasCallerDormantVideo) {
                console.log(
                  "[SipService] Caller has dormant video - will activate after user accepts",
                );

                // Set SDP modifier for re-INVITE response to change a=inactive to a=sendrecv
                // This signals capability but actual video starts after acceptVideoUpgrade()
                session.sessionDescriptionHandlerModifiersReInvite = [
                  (desc: RTCSessionDescriptionInit) =>
                    this.setVideoSendRecv(desc),
                ];
                console.log(
                  "[SipService] Set SDP modifier for re-INVITE response",
                );
              }

              // Store the call ID for later acceptance/rejection
              this.pendingVideoUpgradeCallId = callId;

              // Emit the video upgrade request event inside NgZone to trigger change detection
              this.ngZone.run(() => {
                this._videoUpgradeRequested.next({
                  callId: callId,
                  remoteName: call.name || "Unknown",
                  remoteNumber: call.number,
                });
              });
            }
          }
        }
      },

      // Handle INFO messages that might contain caller ID updates
      onInfo: (info) => {
        console.log("Received INFO message");

        // Extract caller ID headers
        const remotePartyIdHeader = info.request.getHeader("Remote-Party-ID");
        const pAssertedIdHeader = info.request.getHeader("P-Asserted-Identity");

        // Update call with header information
        this.updateCallFromHeaders(
          session,
          remotePartyIdHeader,
          pAssertedIdHeader,
          "INFO message",
        );

        // Automatically accept the INFO message
        info.accept();
      },
    };

    session.stateChange.addListener((state: SessionState) => {
      const callId = this.findCallIdBySession(session);
      if (!callId) return;

      const call = this.calls.get(callId);
      if (!call) return;

      switch (state) {
        case SessionState.Establishing:
          console.log("Call establishing - early media possible");
          call.state = "ringing";
          this.updateActiveCallsList();
          break;
        case SessionState.Established:
          console.log("Call established - enabling audio");
          call.state = "connected";
          // unmute audio when call is established (unless explicitly muted by user)
          this.setTracksEnabled(call, "audio", true);
          call.isMuted = false;

          // Stop ringback tone when call is established
          if (!call.isIncoming) {
            this.stopRingbackTone();
          } else {
            // Stop ringing tone for incoming calls that were just answered
            this.toneService.stopRingingTone();
            // Stop call waiting tone if it was playing
            this.toneService.stopCallWaitingTone();
          }

          // Play call established tone
          this.toneService.playCallEstablishedTone();

          // Apply bitrate limit if this is a video call
          if (call.hasVideo) {
            this.applyVideoBitrateLimit(session);
          }

          this.updateActiveCallsList();
          // Update audio active call
          this.updateAudioActiveCall();
          // Ensure we have a valid active call ID
          this.ensureActiveCallId();
          break;
        case SessionState.Terminated:
          console.log("Call terminated");

          // Stop ringback tone if this was an outgoing call
          if (!call.isIncoming) {
            this.stopRingbackTone();
            this.toneService.playCallEndedTone();
          } else if (
            call.isIncoming &&
            (call.state === "ringing" || call.state === "call-waiting")
          ) {
            // Stop ringing tone or call waiting tone if this was an incoming call that was canceled by the caller
            if (call.isCallWaiting) {
              this.toneService.stopCallWaitingTone();
            } else {
              this.toneService.stopRingingTone();
            }
            console.log(
              "Incoming call was terminated while ringing/waiting - stopping tone",
            );
            this.toneService.playCallEndedTone();

            // Emit an event that other services can subscribe to
            this._incomingCallCanceled.next({
              callId: callId,
              isCallWaiting: call.isCallWaiting || false,
            });
          }

          this.removeCall(callId);
          break;
      }
    });
  }

  /**
   * Update call information based on caller ID headers
   * @param session The SIP session
   * @param remotePartyIdHeader Remote-Party-ID header (if present)
   * @param pAssertedIdHeader P-Asserted-Identity header (if present)
   * @param source Description of where these headers came from (for logging)
   * @param callDirection Optional call direction; if not provided, determined from call's isIncoming property
   */
  private updateCallFromHeaders(
    session: Session,
    remotePartyIdHeader?: string,
    pAssertedIdHeader?: string,
    source: string = "message",
    callDirection?: "inbound" | "outbound",
  ): void {
    // Log available headers for debugging
    if (remotePartyIdHeader) {
      console.log(`Remote-Party-ID in ${source}:`, remotePartyIdHeader);
    }
    if (pAssertedIdHeader) {
      console.log(`P-Asserted-Identity in ${source}:`, pAssertedIdHeader);
    }

    // Determine call direction if not explicitly provided
    let direction = callDirection;
    if (!direction) {
      const callId = this.findCallIdBySession(session);
      if (callId) {
        const call = this.calls.get(callId);
        direction = call?.isIncoming ? "inbound" : "outbound";
      } else {
        // Default to inbound if we can't determine (safer assumption)
        direction = "inbound";
      }
    }

    // Parse caller information with appropriate priority based on call direction
    const { name, number } = this.parseCallerIdHeader(
      remotePartyIdHeader,
      pAssertedIdHeader,
      direction,
    );

    if (name || number) {
      const callId = this.findCallIdBySession(session);

      if (callId) {
        const call = this.calls.get(callId);
        if (call) {
          // Update call information with new caller data
          let updated = false;

          if (name && name !== call.name) {
            call.name = name;
            updated = true;
          }

          if (number && number !== call.number) {
            call.number = number;
            updated = true;
          }

          if (updated) {
            console.log(
              `Updated call ${callId} with new caller information from ${source}`,
            );
            this.updateActiveCallsList();
          }
        }
      }
    }
  }

  async makeCall(number: string, withVideo: boolean = false): Promise<void> {
    this.checkInitialized();

    console.log(
      `[SipService] makeCall called with number=${number}, withVideo=${withVideo}`,
    );

    try {
      // Check video capability (for both video calls and audio calls with dormant video)
      console.log("[SipService] Detecting video capabilities...");
      const videoCapability =
        await this.videoDeviceService.detectVideoCapabilities();
      console.log("[SipService] Video capability:", videoCapability);

      const hasVideoCapability =
        videoCapability.hasCamera && videoCapability.hasVideoCodec;

      // For explicit video calls, require video capability
      if (withVideo && !hasVideoCapability) {
        throw new Error(
          "Video calling not supported: camera or codec unavailable",
        );
      }

      // Determine if we should include dormant video for audio-only calls
      // Include dormant video if we have video capability (camera + codec)
      const includeDormantVideo = !withVideo && hasVideoCapability;

      const target = this.createSipUri(number);
      if (!target) {
        throw new Error("Invalid number");
      }
      console.log("[SipService] SIP URI created:", target.toString());

      // Build media constraints for SIP.js to use when getting media stream
      // Video quality is always 'auto' - browser handles adaptive scaling
      const audioDeviceId =
        this.audioDeviceService.getCurrentAudioInputDeviceValue();
      const videoDeviceId =
        this.videoDeviceService.getCurrentVideoInputDeviceValue();
      const videoProfile = this.videoQualityService.getQualityProfile();

      console.log(
        "[SipService] Media constraints - Audio device:",
        audioDeviceId,
        "Video device:",
        videoDeviceId,
        "Quality: auto",
      );
      console.log(
        `[SipService] withVideo=${withVideo}, includeDormantVideo=${includeDormantVideo}`,
      );

      // For dormant video, we need to provide a dummy video track
      // This allows us to include video m-line without requesting camera access
      let dormantVideoTrack: MediaStreamTrack | null = null;
      if (includeDormantVideo) {
        dormantVideoTrack = this.createDormantVideoTrack();
        console.log(
          "[SipService] Created dormant video track for audio-only call",
        );
      }

      // Build inviter options based on call type
      let inviterOptions: InviterOptions;

      if (withVideo) {
        // Active video call - use camera with full constraints
        // Use 'ideal' for audio device to allow fallback to default if selected device is unavailable
        inviterOptions = {
          sessionDescriptionHandlerOptions: {
            constraints: {
              audio:
                audioDeviceId && audioDeviceId !== "default"
                  ? { deviceId: { ideal: audioDeviceId } }
                  : true,
              video: {
                deviceId:
                  videoDeviceId && videoDeviceId !== "default"
                    ? { exact: videoDeviceId }
                    : undefined,
                width: { ideal: videoProfile.width },
                height: { ideal: videoProfile.height },
                frameRate: { ideal: videoProfile.maxFramerate },
                aspectRatio: { ideal: videoProfile.aspectRatio },
              },
            },
          },
        };
      } else if (includeDormantVideo) {
        // Audio-only call WITH dormant video capability
        // Create a stream with audio (from mic) + dummy video (from canvas)
        // This avoids camera permission request entirely!
        console.log(
          "[SipService] Creating pre-acquired stream with dummy video for dormant video call",
        );

        // Get audio stream (microphone only)
        const audioStream = await this.audioDeviceService.getAudioStream();

        // Create combined stream with audio + dummy video
        const dormantStream = new MediaStream();
        audioStream
          .getAudioTracks()
          .forEach((track) => dormantStream.addTrack(track));
        dormantStream.addTrack(dormantVideoTrack!);

        // Store the stream so our custom mediaStreamFactory will use it
        // instead of calling getUserMedia
        this.pendingDormantVideoStream = dormantStream;
        console.log(
          "[SipService] Set pendingDormantVideoStream for next invite",
        );

        // Tell SIP.js we want audio + video (so it creates the m-lines)
        // Our custom mediaStreamFactory will intercept and return our pre-made stream
        inviterOptions = {
          sessionDescriptionHandlerOptions: {
            constraints: {
              audio: true, // Triggers mediaStreamFactory call
              video: true, // Triggers mediaStreamFactory call with video m-line
            },
          },
          // Modify SDP to set video direction to inactive
          sessionDescriptionHandlerModifiers: [
            (description) => this.setVideoInactive(description),
          ],
        };
      } else {
        // Pure audio-only call (no video capability on our side)
        // Use 'ideal' for audio device to allow fallback to default if selected device is unavailable
        inviterOptions = {
          sessionDescriptionHandlerOptions: {
            constraints: {
              audio:
                audioDeviceId && audioDeviceId !== "default"
                  ? { deviceId: { ideal: audioDeviceId } }
                  : true,
              video: false,
            },
          },
        };
      }

      console.log("[SipService] Creating Inviter...");
      const inviter = new Inviter(this.userAgent, target, inviterOptions);
      this.setupSessionDelegates(inviter);

      const callId = Math.random().toString(36).substr(2, 9);
      console.log("[SipService] Generated call ID:", callId);

      console.log("[SipService] Adding call to calls map...");
      this.calls.set(callId, {
        session: inviter,
        number,
        name: "",
        state: "ringing",
        hasVideo: withVideo,
        hasRemoteVideo: false,
        localStream: null, // Will be populated when SessionDescriptionHandler is created
        remoteStream: null,
        isMuted: true,
        isVideoMuted: false,
        videoQuality: "auto",
        videoCapabilities: {
          local: hasVideoCapability,
          remote: false, // Will be updated when remote stream arrives
        },
        // Dormant video flags will be confirmed after we check the remote's SDP answer
        hasCallerDormantVideo: false, // Will be set to true if remote accepts video m-line (caller only)
        canUpgradeToVideo: false, // UI flag for upgrade button
      });

      console.log("[SipService] Updating active calls list...");
      this.updateActiveCallsList();
      console.log("[SipService] Setting active call ID...");
      this.activeCallId.next(callId);
      // Set audio active call for new outgoing call
      this.updateAudioActiveCall();
      console.log("[SipService] Active calls count:", this.calls.size);

      const inviteOptions = {
        requestDelegate: {
          onProgress: (response: any) => {
            // Triggered on provisional responses like 180 Ringing or 183 Session Progress
            console.log(
              `Received provisional response: ${response.message.statusCode}`,
            );

            // Start ringback tone when we get a provisional response
            if (
              (response.message.statusCode === 180 ||
                response.message.statusCode === 183) &&
              !this.isRingbackPlaying
            ) {
              this.startRingbackTone();
            }

            // Extract caller ID headers
            const remotePartyIdHeader =
              response.message.getHeader("Remote-Party-ID");
            const pAssertedIdHeader = response.message.getHeader(
              "P-Asserted-Identity",
            );

            if (remotePartyIdHeader || pAssertedIdHeader) {
              // Update call with header information (outbound call receiving response)
              this.updateCallFromHeaders(
                inviter,
                remotePartyIdHeader,
                pAssertedIdHeader,
                `provisional response ${response.message.statusCode}`,
                "outbound",
              );
            }
          },
          onAccept: (response: any) => {
            // Check if remote accepted video in their SDP answer
            if (includeDormantVideo) {
              const call = this.calls.get(callId);
              if (call) {
                const remoteSdp = response.message.body;
                const videoAccepted = this.checkVideoAcceptedInSDP(remoteSdp);
                call.hasCallerDormantVideo = videoAccepted;
                call.canUpgradeToVideo = videoAccepted;
                console.log(
                  `[SipService] Remote ${videoAccepted ? "accepted" : "rejected"} dormant video`,
                );

                if (videoAccepted) {
                  // Disable the video sender to ensure zero bandwidth
                  this.disableVideoSender(inviter);
                }

                this.updateActiveCallsList();
              }
            }
          },
          onReject: (response: any) => {
            const statusCode = response.message.statusCode;
            console.log(
              `[SipService] Outgoing call rejected with status: ${statusCode}`,
            );

            // Handle busy responses: 486 Busy Here, 600 Busy Everywhere, 603 Decline
            if (
              statusCode === 486 ||
              statusCode === 600 ||
              statusCode === 603
            ) {
              this.stopRingbackTone();

              // SIP.js fires Terminated BEFORE onReject, so the call may
              // already be removed. Re-add a minimal entry for busy display.
              if (!this.calls.has(callId)) {
                this.calls.set(callId, {
                  session: inviter,
                  number,
                  name: "",
                  state: "busy",
                  isIncoming: false,
                  hasVideo: false,
                  hasRemoteVideo: false,
                  localStream: null,
                  remoteStream: null,
                  isMuted: false,
                  isVideoMuted: false,
                });
              } else {
                const call = this.calls.get(callId)!;
                call.state = "busy";
              }

              this.updateActiveCallsList();
              this.activeCallId.next(callId);

              // Play busy tone (auto-stops after 3 seconds)
              this.toneService.startBusyTone(3000);

              // Auto-remove the call after the busy tone finishes
              const timeout = setTimeout(() => {
                this.toneService.stopBusyTone();
                this.removeCall(callId);
                this.busyCallTimeouts.delete(callId);
              }, 3000);
              this.busyCallTimeouts.set(callId, timeout);
            }
          },
        },
      };

      console.log("[SipService] Sending INVITE...");
      await inviter.invite(inviteOptions);
      console.log(
        "[SipService] INVITE sent successfully, call is now in progress",
      );
    } catch (error) {
      // Check if this is an audio device error (OverconstrainedError or NotFoundError)
      if (
        error instanceof OverconstrainedError ||
        (error instanceof DOMException &&
          (error.name === "OverconstrainedError" ||
            error.name === "NotFoundError"))
      ) {
        console.error(
          "[SipService] Audio input device unavailable for call:",
          error,
        );
        this.audioDeviceService.markAudioInputUnavailable();
      } else {
        console.error("[SipService] Error making call:", error);
      }
      throw error;
    }
  }

  /**
   * Detect if SDP contains ACTIVE video media line
   * @param sdp The SDP string to parse
   * @returns True if active video is present in SDP (not dormant/inactive)
   */
  private detectVideoInSDP(sdp: string): boolean {
    if (!sdp) return false;

    // Look for video media line (m=video) and check if it's active
    const lines = sdp.split("\r\n");
    let inVideoSection = false;
    let videoPort = "0";

    for (const line of lines) {
      if (line.startsWith("m=video")) {
        // Extract port from m=video line: m=video <port> ...
        const parts = line.split(" ");
        if (parts.length >= 2) {
          videoPort = parts[1];
          inVideoSection = true;
        }
      } else if (line.startsWith("m=") && inVideoSection) {
        // We've moved to another media section, check what we found
        break;
      } else if (inVideoSection && line.startsWith("a=")) {
        // Check for inactive direction - dormant video has a=inactive
        if (line === "a=inactive") {
          // Video m-line exists but is inactive (dormant) - not active video
          return false;
        }
        // sendrecv, sendonly, recvonly all indicate active video
        if (
          line === "a=sendrecv" ||
          line === "a=sendonly" ||
          line === "a=recvonly"
        ) {
          // Active video found - check port too
          return videoPort !== "0";
        }
      }
    }

    // If we found a video section with non-zero port but didn't find any direction attribute,
    // assume it's active (default is sendrecv)
    return inVideoSection && videoPort !== "0";
  }

  /**
   * Check if caller offered video in SDP (even if dormant/inactive)
   * This is different from detectVideoInSDP which only returns true for active video
   * @param sdp The SDP string to parse
   * @returns True if video m-line exists with non-zero port (regardless of direction)
   */
  private checkIfCallerOfferedVideo(sdp: string): boolean {
    if (!sdp) return false;

    // Look for video media line (m=video) with non-zero port
    const lines = sdp.split("\r\n");
    for (const line of lines) {
      if (line.startsWith("m=video")) {
        const parts = line.split(" ");
        if (parts.length >= 2) {
          const port = parts[1];
          // Port 0 means video is disabled/rejected
          if (port !== "0") {
            return true;
          }
        }
      }
    }

    return false;
  }

  /**
   * Parse caller ID information from SIP headers
   *
   * Priority for inbound calls:
   *   1. P-Asserted-Identity (with party=calling or no party param)
   *   2. Remote-Party-ID (legacy fallback)
   *   3. From header (handled externally as default)
   *
   * Priority for outbound calls:
   *   1. P-Asserted-Identity (with party=called or no party param)
   *   2. Remote-Party-ID (legacy fallback)
   *   3. To header (handled externally as default)
   *
   * @param remotePartyIdHeader The Remote-Party-ID header value
   * @param pAssertedIdHeader The P-Asserted-Identity header value
   * @param callDirection Whether this is an inbound or outbound call
   * @returns An object containing the parsed name and number
   */
  private parseCallerIdHeader(
    remotePartyIdHeader?: string,
    pAssertedIdHeader?: string,
    callDirection: "inbound" | "outbound" = "inbound",
  ): { name: string; number: string } {
    // Initialize with empty values
    let name = "";
    let number = "";

    /**
     * Extract party= parameter from header (optional parameter)
     * Example: "John Doe" <sip:1234@example.com>;party=calling
     * @returns 'calling', 'called', or null if not present
     */
    const getPartyParam = (header: string): string | null => {
      const partyMatch = header.match(/;party=(\w+)/i);
      return partyMatch ? partyMatch[1].toLowerCase() : null;
    };

    /**
     * Check if header is appropriate for the call direction based on party= param
     * - For inbound calls: accept party=calling or no party param
     * - For outbound calls: accept party=called or no party param
     * - If party= is absent, header is always appropriate (legacy behavior)
     */
    const isHeaderAppropriate = (
      header: string,
      direction: "inbound" | "outbound",
    ): boolean => {
      const party = getPartyParam(header);
      if (!party) {
        // No party parameter - always appropriate (legacy/default behavior)
        return true;
      }
      if (direction === "inbound") {
        // For inbound calls, we want the caller's identity (party=calling)
        return party === "calling";
      } else {
        // For outbound calls, we want the called party's identity (party=called)
        return party === "called";
      }
    };

    // Determine which header to use
    // Priority: P-Asserted-Identity > Remote-Party-ID (for legacy compatibility)
    let headerToUse: string | undefined;
    let headerSource: string = "";

    // Try P-Asserted-Identity first (highest priority per RFC 3325)
    if (
      pAssertedIdHeader &&
      isHeaderAppropriate(pAssertedIdHeader, callDirection)
    ) {
      headerToUse = pAssertedIdHeader;
      headerSource = "P-Asserted-Identity";
    }
    // Fall back to Remote-Party-ID (legacy compatibility)
    else if (
      remotePartyIdHeader &&
      isHeaderAppropriate(remotePartyIdHeader, callDirection)
    ) {
      headerToUse = remotePartyIdHeader;
      headerSource = "Remote-Party-ID";
    }

    // Log header selection decision
    if (headerToUse) {
      const party = getPartyParam(headerToUse);
      const partyInfo = party ? ` (party=${party})` : " (no party param)";
      console.log(
        `Using ${headerSource} for ${callDirection} call${partyInfo}:`,
        headerToUse,
      );
    } else if (pAssertedIdHeader || remotePartyIdHeader) {
      // Headers exist but none were appropriate for this direction
      if (pAssertedIdHeader) {
        const party = getPartyParam(pAssertedIdHeader);
        console.log(
          `Skipping P-Asserted-Identity (party=${party}, need ${callDirection === "inbound" ? "calling" : "called"} or none):`,
          pAssertedIdHeader,
        );
      }
      if (remotePartyIdHeader) {
        const party = getPartyParam(remotePartyIdHeader);
        console.log(
          `Skipping Remote-Party-ID (party=${party}, need ${callDirection === "inbound" ? "calling" : "called"} or none):`,
          remotePartyIdHeader,
        );
      }
    }

    if (!headerToUse) {
      return { name, number };
    }

    // Extract the name part (usually in quotes)
    // Example: "John Doe" <sip:1234@example.com>;party=calling
    const nameMatch = headerToUse.match(/"([^"]+)"/);
    if (nameMatch && nameMatch[1]) {
      name = nameMatch[1];
    }

    // If no quoted name, try unquoted name before URI
    // Example: John Doe <sip:1234@example.com>
    if (!name) {
      const altNameMatch = headerToUse.match(/([^<]+)</);
      if (altNameMatch && altNameMatch[1]) {
        name = altNameMatch[1].trim();
      }
    }

    // Extract the SIP URI and get the user part (phone number)
    // Example: <sip:1234@example.com>
    const numberMatch = headerToUse.match(/<sip:([^@>]+)@/);
    if (numberMatch && numberMatch[1]) {
      number = numberMatch[1];
    }

    // Handle tel: URI format (common in P-Asserted-Identity)
    // Example: "John Doe" <tel:+1234567890>
    if (!number) {
      const telMatch = headerToUse.match(/<tel:([^>]+)>/);
      if (telMatch && telMatch[1]) {
        number = telMatch[1];
      }
    }

    return { name, number };
  }

  private async handleIncomingCall(invitation: Invitation): Promise<void> {
    // If DND mode is enabled, automatically reject the call with a 480 Temporarily Unavailable
    if (this._isDoNotDisturbEnabled) {
      console.log("Automatically rejecting incoming call due to DND mode");
      try {
        // 480 Temporarily Unavailable is the appropriate response for DND mode
        await invitation.reject({
          statusCode: 480,
          reasonPhrase: "Do Not Disturb",
        });
      } catch (error) {
        console.error("Error rejecting incoming call in DND mode:", error);
      }
      return;
    }

    // Start ringing/call waiting tone IMMEDIATELY before any async operations
    // This ensures the user hears the call even if browser permission dialogs appear
    const isCallWaiting = this.hasActiveCalls();
    if (isCallWaiting) {
      console.log(
        "Starting call waiting tone for incoming call (active calls present)",
      );
      this.toneService.startCallWaitingTone();
    } else {
      console.log("Starting ringing tone for incoming call (no active calls)");
      this.toneService.startRingingTone();
    }

    try {
      // Detect if incoming call has video by parsing SDP
      const hasIncomingVideo = this.detectVideoInSDP(invitation.request.body);

      const mediaStream = await this.audioDeviceService.getAudioStream();

      // Get caller number from SIP URI (default fallback)
      let callerNumber = invitation.remoteIdentity.uri.user || "";
      let callerName = "";

      // Get and parse the caller ID headers
      const remotePartyIdHeader =
        invitation.request.getHeader("Remote-Party-ID");
      const pAssertedIdHeader = invitation.request.getHeader(
        "P-Asserted-Identity",
      );

      // Parse caller information (priority: P-Asserted-Identity > Remote-Party-ID > From)
      const { name, number } = this.parseCallerIdHeader(
        remotePartyIdHeader,
        pAssertedIdHeader,
        "inbound",
      );

      // Use the parsed values if available
      if (name) callerName = name;
      if (number) callerNumber = number;

      // Log headers for debugging
      if (remotePartyIdHeader || pAssertedIdHeader) {
        this.updateCallFromHeaders(
          invitation,
          remotePartyIdHeader,
          pAssertedIdHeader,
          "initial INVITE",
          "inbound",
        );
      }

      console.log(
        `Parsed caller information - Name: '${callerName}', Number: '${callerNumber}', Has Video: ${hasIncomingVideo}`,
      );

      const callId = Math.random().toString(36).substr(2, 9);

      // Detect local video capability
      const videoCapability =
        await this.videoDeviceService.detectVideoCapabilities();
      const canAcceptVideo =
        videoCapability.hasCamera && videoCapability.hasVideoCodec;

      this.calls.set(callId, {
        session: invitation,
        number: callerNumber || "unknown",
        name: callerName,
        state: isCallWaiting ? "call-waiting" : "ringing",
        isIncoming: true,
        hasVideo: false,
        hasRemoteVideo: hasIncomingVideo,
        localStream: mediaStream,
        remoteStream: null,
        isMuted: true,
        isVideoMuted: false,
        isCallWaiting: isCallWaiting,
        videoQuality: "auto",
        videoCapabilities: {
          local: canAcceptVideo,
          remote: hasIncomingVideo,
        },
      });

      this.setupSessionDelegates(invitation);
      this.incomingInvitation = invitation;

      // For call waiting calls, always set as active call since they need immediate user interaction
      // For regular incoming calls, only set as active if no active call exists
      if (isCallWaiting || !this.activeCallId.value) {
        this.activeCallId.next(callId);
        if (isCallWaiting) {
          console.log(
            `Call waiting call ${callId} set as active call for user interaction`,
          );
        }
      }

      this.updateActiveCallsList();

      // Don't change audio active call for call waiting - keep the connected call's audio active
      if (!isCallWaiting) {
        this.updateAudioActiveCall();
      }
    } catch (error) {
      console.error("Error handling incoming call:", error);
      // Check if this is an audio device error
      if (
        error instanceof OverconstrainedError ||
        (error instanceof DOMException &&
          (error.name === "OverconstrainedError" ||
            error.name === "NotFoundError"))
      ) {
        console.error(
          "[SipService] Audio input device unavailable for incoming call:",
          error,
        );
        this.audioDeviceService.markAudioInputUnavailable();
      }
      // Stop the ringing tone since we're rejecting the call due to error
      if (isCallWaiting) {
        this.toneService.stopCallWaitingTone();
      } else {
        this.toneService.stopRingingTone();
      }
      await invitation.reject();
    }
  }

  async acceptCall(): Promise<void> {
    if (!this.incomingInvitation) {
      throw new Error("No incoming call to accept");
    }

    try {
      // Find the call that matches this invitation
      const callEntry = Array.from(this.calls.entries()).find(
        ([_, call]) => call.session === this.incomingInvitation,
      );

      if (!callEntry) {
        throw new Error("Could not find call matching incoming invitation");
      }

      const [callId, call] = callEntry;

      // Determine if we should accept with video
      const shouldAcceptWithVideo =
        call.hasRemoteVideo && call.videoCapabilities?.local === true;

      // Check if caller offered video (even if dormant/inactive) - parse original SDP
      // This allows us to include dormant video in our response for future upgrade capability
      const callerOfferedVideo = this.checkIfCallerOfferedVideo(
        this.incomingInvitation.request.body,
      );
      const canIncludeDormantVideo =
        !shouldAcceptWithVideo &&
        callerOfferedVideo &&
        call.videoCapabilities?.local === true;

      console.log(
        `[SipService] Accepting call ${callId} with video: ${shouldAcceptWithVideo}, include dormant video: ${canIncludeDormantVideo} (remote has active video: ${call.hasRemoteVideo}, caller offered video: ${callerOfferedVideo}, local can accept: ${call.videoCapabilities?.local})`,
      );

      // Stop the ringing tone or call waiting tone
      this.toneService.stopRingingTone();
      this.toneService.stopCallWaitingTone();

      // If accepting with video, get video stream first
      if (shouldAcceptWithVideo) {
        try {
          // Get video quality for acceptance (use auto or saved quality)
          const quality = call.videoQuality || "auto";
          console.log(
            `[SipService] Getting video stream with quality: ${quality}`,
          );

          const videoStream = await this.videoDeviceService.getVideoStream(
            quality === "auto" ? "hd" : quality, // Use HD for auto, otherwise use specified quality
            undefined, // Use default camera
          );

          // Add video track to local stream
          const videoTrack = videoStream.getVideoTracks()[0];
          if (videoTrack && call.localStream) {
            call.localStream.addTrack(videoTrack);
            console.log(
              `[SipService] Added video track to local stream:`,
              videoTrack.id,
            );
          }

          // Update call to reflect video
          call.hasVideo = true;
          call.isVideoMuted = false;
        } catch (videoError) {
          console.error(
            "[SipService] Failed to get video stream, accepting audio-only:",
            videoError,
          );
          // Fall back to audio-only
        }
      } else if (canIncludeDormantVideo) {
        // Accept audio-only but include dormant video for future upgrade capability
        // IMPORTANT: We use pendingDormantVideoStream to avoid triggering camera permission
        // NOTE: We do NOT set hasCallerDormantVideo=true for callee - when caller upgrades, the
        // acceptVideoUpgrade() should use the "NO DORMANT VIDEO" path which correctly
        // finds the existing video sender from the auto-accepted re-INVITE.
        // We DO set canUpgradeToVideo=true so the UI shows the upgrade button.
        console.log(
          `[SipService] Including dormant video in response for future upgrade capability`,
        );

        // Create dormant video track (canvas-based, no camera access)
        const dormantTrack = this.createDormantVideoTrack();

        // Create a combined stream with existing audio + dormant video
        // This will be used by custom mediaStreamFactory instead of getUserMedia
        const combinedStream = new MediaStream();

        // Add audio tracks from existing local stream
        if (call.localStream) {
          call.localStream.getAudioTracks().forEach((track) => {
            combinedStream.addTrack(track);
          });
        }

        // Add dormant video track
        combinedStream.addTrack(dormantTrack);

        // Set as pending stream - custom mediaStreamFactory will use this
        this.pendingDormantVideoStream = combinedStream;

        // Update call's local stream
        call.localStream = combinedStream;

        console.log(
          `[SipService] Created combined stream with dormant video (no camera access)`,
        );
      }

      // Determine video constraint for accept
      const videoConstraint = shouldAcceptWithVideo || canIncludeDormantVideo;

      await this.incomingInvitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: videoConstraint,
          },
        },
        sessionDescriptionHandlerModifiers: canIncludeDormantVideo
          ? [(desc: RTCSessionDescriptionInit) => this.setVideoInactive(desc)]
          : undefined,
      });

      // For dormant video on callee: do NOT set hasCallerDormantVideo = true
      // This is intentional - when caller upgrades, we want acceptVideoUpgrade() to use
      // the "NO DORMANT VIDEO" path which correctly finds the existing video sender
      // created by SIP.js auto-accept of the re-INVITE.
      // We DO set canUpgradeToVideo=true so the UI shows the upgrade button for callee.
      if (canIncludeDormantVideo) {
        call.canUpgradeToVideo = true; // UI flag - shows upgrade button
        // hasCallerDormantVideo stays false - callee uses different upgrade path
        // Disable the video sender to ensure zero bandwidth for dormant video
        this.disableVideoSender(call.session).catch((err) => {
          console.warn(
            "[SipService] Failed to disable dormant video sender:",
            err,
          );
        });
        console.log(
          `[SipService] Call ${callId} accepted with dormant video in SDP (canUpgradeToVideo=true, hasCallerDormantVideo=false - callee uses NO DORMANT path for upgrades)`,
        );
        this.updateActiveCallsList();
      }

      this.incomingInvitation = null;
    } catch (error) {
      if (
        error instanceof OverconstrainedError ||
        (error instanceof DOMException &&
          (error.name === "OverconstrainedError" ||
            error.name === "NotFoundError"))
      ) {
        console.error(
          "[SipService] Audio input device unavailable when accepting call:",
          error,
        );
        this.audioDeviceService.markAudioInputUnavailable();
      } else {
        console.error("Error accepting call:", error);
      }
      throw error;
    }
  }

  /**
   * Accept an incoming call with audio only (receive remote video but don't send local video)
   * This creates an asymmetric video state where the callee can see the caller's video
   * but the caller sees a black screen (or video muted indicator) for the callee
   */
  async acceptCallAudioOnly(): Promise<void> {
    if (!this.incomingInvitation) {
      throw new Error("No incoming call to accept");
    }

    try {
      // Find the call that matches this invitation
      const callEntry = Array.from(this.calls.entries()).find(
        ([_, call]) => call.session === this.incomingInvitation,
      );

      if (!callEntry) {
        throw new Error("Could not find call matching incoming invitation");
      }

      const [callId, call] = callEntry;

      console.log(
        `[SipService] Accepting call ${callId} as audio-only (will receive remote video but not send local video)`,
      );

      // Stop the ringing tone or call waiting tone
      this.toneService.stopRingingTone();
      this.toneService.stopCallWaitingTone();

      // Accept without enabling local video
      // The remote will send video (hasRemoteVideo = true) but we won't send video (hasVideo = false)
      await this.incomingInvitation.accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false, // Don't request local video
          },
        },
      });

      // Update call state to reflect asymmetric video
      call.hasVideo = false; // Not sending video
      // call.hasRemoteVideo remains true (will receive remote video)
      call.isVideoMuted = false; // Not technically muted, just not sending

      this.incomingInvitation = null;

      console.log(
        `[SipService] Call ${callId} accepted as audio-only. Remote video will be received.`,
      );
    } catch (error) {
      if (
        error instanceof OverconstrainedError ||
        (error instanceof DOMException &&
          (error.name === "OverconstrainedError" ||
            error.name === "NotFoundError"))
      ) {
        console.error(
          "[SipService] Audio input device unavailable when accepting audio-only call:",
          error,
        );
        this.audioDeviceService.markAudioInputUnavailable();
      } else {
        console.error("Error accepting call as audio-only:", error);
      }
      throw error;
    }
  }

  /**
   * Accept a call waiting call and put active calls on hold
   */
  async acceptCallWaiting(
    callId: string,
    audioOnly: boolean = false,
  ): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("Call not found");
    }

    if (!call.isCallWaiting || call.state !== "call-waiting") {
      throw new Error("Call is not in call waiting state");
    }

    try {
      console.log(
        `Accepting call waiting call ${callId}${audioOnly ? " (audio-only)" : ""}`,
      );

      // First, put all connected calls on hold
      const connectedCalls = Array.from(this.calls.entries()).filter(
        ([_, activeCall]) => activeCall.state === "connected",
      );

      for (const [id, activeCall] of connectedCalls) {
        console.log(`Putting call ${id} on hold to accept call waiting`);
        try {
          await this.holdCall(id);
        } catch (holdError) {
          console.warn(`Failed to hold call ${id}:`, holdError);
          // Continue with acceptance even if hold fails
        }
      }

      // Stop call waiting tone
      this.toneService.stopCallWaitingTone();

      // Determine if we should accept with video
      const shouldAcceptWithVideo =
        !audioOnly &&
        call.hasRemoteVideo &&
        call.videoCapabilities?.local === true;

      console.log(
        `[SipService] Accepting call waiting ${callId} with video: ${shouldAcceptWithVideo} (audio-only requested: ${audioOnly}, remote has video: ${call.hasRemoteVideo}, local can accept: ${call.videoCapabilities?.local})`,
      );

      // If accepting with video, get video stream first
      if (shouldAcceptWithVideo) {
        try {
          // Get video quality for acceptance (use auto or saved quality)
          const quality = call.videoQuality || "auto";
          console.log(
            `[SipService] Getting video stream with quality: ${quality}`,
          );

          const videoStream = await this.videoDeviceService.getVideoStream(
            quality === "auto" ? "hd" : quality, // Use HD for auto, otherwise use specified quality
            undefined, // Use default camera
          );

          // Add video track to local stream
          const videoTrack = videoStream.getVideoTracks()[0];
          if (videoTrack && call.localStream) {
            call.localStream.addTrack(videoTrack);
            console.log(
              `[SipService] Added video track to local stream:`,
              videoTrack.id,
            );
          }

          // Update call to reflect video
          call.hasVideo = true;
          call.isVideoMuted = false;
        } catch (videoError) {
          console.error(
            "[SipService] Failed to get video stream, accepting audio-only:",
            videoError,
          );
          // Fall back to audio-only
        }
      }

      // Accept the call waiting call
      await (call.session as Invitation).accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: shouldAcceptWithVideo,
          },
        },
      });

      // Update call state
      call.isCallWaiting = false;
      call.state = "connected";

      // If accepted as audio-only, ensure video state is correct
      if (audioOnly && call.hasRemoteVideo) {
        call.hasVideo = false; // Not sending video
        call.isVideoMuted = false; // Not technically muted, just not sending
      }

      this.activeCallId.next(callId);
      this.updateActiveCallsList();
      // Update audio active call when accepting call waiting
      this.updateAudioActiveCall();

      console.log(`Successfully accepted call waiting call ${callId}`);
    } catch (error) {
      if (
        error instanceof OverconstrainedError ||
        (error instanceof DOMException &&
          (error.name === "OverconstrainedError" ||
            error.name === "NotFoundError"))
      ) {
        console.error(
          "[SipService] Audio input device unavailable when accepting call waiting:",
          error,
        );
        this.audioDeviceService.markAudioInputUnavailable();
      } else {
        console.error("Error accepting call waiting:", error);
      }
      // Ensure call waiting tone is stopped even on error
      this.toneService.stopCallWaitingTone();
      throw error;
    }
  }

  /**
   * Accept a video upgrade request from the remote party
   * This enables local video via session renegotiation
   */
  async acceptVideoUpgrade(callId: string): Promise<void> {
    if (this.pendingVideoUpgradeCallId !== callId) {
      throw new Error("No pending video upgrade request for this call");
    }

    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("Call not found");
    }

    try {
      console.log(`[SipService] Accepting video upgrade for call ${callId}`);

      // Get video stream from camera
      const quality = call.videoQuality || "auto";
      const videoStream = await this.videoDeviceService.getVideoStream(
        quality === "auto" ? "auto" : quality,
        undefined, // Use default camera
      );

      const videoTrack = videoStream.getVideoTracks()[0];
      if (!videoTrack) {
        throw new Error("Failed to get video track from camera");
      }

      const sdh = call.session.sessionDescriptionHandler as CustomSDH;
      if (sdh && sdh.peerConnection) {
        const senders = sdh.peerConnection.getSenders();
        const videoSender = senders.find(
          (s) =>
            s.track?.kind === "video" || (s.track === null && s.dtmf === null),
        );

        if (videoSender && call.hasCallerDormantVideo) {
          // DORMANT VIDEO PATH: Replace the dormant track with real camera track
          console.log(
            `[SipService] Replacing dormant video track with camera track`,
          );

          // Replace the track
          await videoSender.replaceTrack(videoTrack);
          console.log(`[SipService] Track replaced with camera track`);

          // Update local stream
          if (call.localStream) {
            const oldVideoTracks = call.localStream.getVideoTracks();
            oldVideoTracks.forEach((track) => {
              call.localStream?.removeTrack(track);
              track.stop();
            });
            call.localStream.addTrack(videoTrack);
          } else {
            call.localStream = videoStream;
          }

          // Enable video sender (may have been disabled for dormant video)
          await this.enableVideoSender(call.session);
          console.log(`[SipService] Video sender enabled`);

          // CRITICAL: Set transceiver direction to sendrecv BEFORE creating the re-INVITE SDP.
          // The SDP modifier only changes the SDP string after generation, but WebRTC uses
          // the transceiver.direction property to determine actual RTP flow.
          // If direction is still 'inactive', no RTP will be sent regardless of SDP content.
          const transceivers = sdh.peerConnection.getTransceivers();
          const videoTransceiver = transceivers.find(
            (t) =>
              t.sender === videoSender || t.receiver?.track?.kind === "video",
          );
          if (videoTransceiver) {
            console.log(
              `[SipService] Setting video transceiver direction from '${videoTransceiver.direction}' to 'sendrecv'`,
            );
            videoTransceiver.direction = "sendrecv";
          } else {
            console.warn(
              `[SipService] Could not find video transceiver to set direction`,
            );
          }

          // Send a re-INVITE to inform the remote party that we're now actually sending video.
          // The auto-accept of their re-INVITE already sent 200 OK (possibly with a=inactive),
          // so we need this re-INVITE to properly negotiate sendrecv.
          console.log(
            `[SipService] Sending re-INVITE to activate video transmission`,
          );
          try {
            // Both modifiers in one array:
            // - setVideoSendRecv: Changes a=inactive to a=sendrecv (for offer)
            // - fixMissingIceCredentials: Copies ICE from audio to video if missing (for answer from SDES clients)
            await call.session.invite({
              sessionDescriptionHandlerModifiers: [
                (desc: RTCSessionDescriptionInit) =>
                  this.setVideoSendRecv(desc),
                (desc: RTCSessionDescriptionInit) =>
                  this.fixMissingIceCredentials(desc),
              ],
            });
            console.log(
              `[SipService] Re-INVITE sent successfully to activate video`,
            );
          } catch (reInviteError) {
            // Log but don't fail - the track is already replaced, video may work anyway
            console.warn(
              `[SipService] Re-INVITE failed, video may still work:`,
              reInviteError,
            );
          }

          // Apply bitrate limit after re-INVITE completes (setParameters after negotiation)
          await this.videoQualityService.applyBitrateLimit(videoSender);

          console.log(
            `[SipService] Successfully replaced dormant track with camera track`,
          );
        } else {
          // NO DORMANT VIDEO PATH (callee accepting video upgrade)
          // The caller's re-INVITE was auto-accepted by SIP.js, creating a new video transceiver.
          // IMPORTANT: There may be multiple video transceivers:
          // - One from our dormant video in acceptCall() (mid=null, not negotiated)
          // - One from the auto-accepted re-INVITE (mid=1, negotiated with currentDirection=sendrecv)
          // We need to find the NEGOTIATED transceiver (the one with a mid) and replace ITS sender's track.
          console.log(
            `[SipService] No dormant video - looking for negotiated video transceiver from re-INVITE`,
          );

          // Find the negotiated video transceiver (has a mid assigned)
          const transceivers = sdh.peerConnection.getTransceivers();
          const negotiatedVideoTransceiver = transceivers.find(
            (t) => t.mid !== null && t.receiver?.track?.kind === "video",
          );

          const existingVideoSender = negotiatedVideoTransceiver?.sender;

          if (existingVideoSender) {
            // Use replaceTrack on the existing video sender
            console.log(
              `[SipService] Found existing video sender, replacing track`,
            );
            await existingVideoSender.replaceTrack(videoTrack);

            // Update local stream - replace old video track (dormant) with new camera track
            if (call.localStream) {
              // Remove old video tracks (dormant canvas tracks) and stop them
              const oldVideoTracks = call.localStream.getVideoTracks();
              oldVideoTracks.forEach((track) => {
                console.log(
                  `[SipService] Removing old video track from localStream:`,
                  track.id,
                );
                call.localStream?.removeTrack(track);
                track.stop();
              });
              // Add the new camera track
              call.localStream.addTrack(videoTrack);
            } else {
              call.localStream = videoStream;
            }

            console.log(
              `[SipService] Replaced video track on existing sender:`,
              videoTrack.id,
            );

            // Ensure transceiver direction is sendrecv (not recvonly or inactive)
            // We already have the negotiatedVideoTransceiver from above
            if (negotiatedVideoTransceiver) {
              console.log(
                `[SipService] Video transceiver mid=${negotiatedVideoTransceiver.mid}, direction: ${negotiatedVideoTransceiver.direction}, currentDirection: ${negotiatedVideoTransceiver.currentDirection}`,
              );
              if (negotiatedVideoTransceiver.direction !== "sendrecv") {
                console.log(
                  `[SipService] Setting video transceiver direction to 'sendrecv'`,
                );
                negotiatedVideoTransceiver.direction = "sendrecv";
              }
            }

            // Enable the video sender (in case it was disabled for dormant video)
            // This is important for the callee path where we included dormant video in acceptCall()
            await this.enableVideoSender(call.session);
            console.log(`[SipService] Video sender enabled`);

            // NOTE: We do NOT send a re-INVITE here because:
            // 1. The callee already sent a 200 OK with a=sendrecv when auto-accepting the caller's re-INVITE
            // 2. The video transceiver already exists and is properly negotiated
            // 3. replaceTrack() is a local-only operation that doesn't require renegotiation
            // 4. Sending a re-INVITE would create a NEW video m-line (3rd m-line) which gets rejected

            // Apply bitrate limit
            await this.videoQualityService.applyBitrateLimit(
              existingVideoSender,
            );
          } else {
            // Fallback: No existing video sender found, use addTrack
            // This shouldn't happen if caller sent a video re-INVITE, but handle it anyway
            console.log(
              `[SipService] No existing video sender found, adding new track (fallback)`,
            );

            if (call.localStream) {
              sdh.peerConnection.addTrack(videoTrack, call.localStream);
              call.localStream.addTrack(videoTrack);
            } else {
              call.localStream = videoStream;
              sdh.peerConnection.addTrack(videoTrack, videoStream);
            }

            console.log(
              `[SipService] Added video track to peer connection:`,
              videoTrack.id,
            );

            // Send re-INVITE only in fallback case where we had to add a new track
            console.log(
              `[SipService] Sending re-INVITE to activate video transmission (fallback path)`,
            );
            try {
              // Both modifiers in one array:
              // - setVideoSendRecv: Changes a=inactive to a=sendrecv (for offer)
              // - fixMissingIceCredentials: Copies ICE from audio to video if missing (for answer from SDES clients)
              await call.session.invite({
                sessionDescriptionHandlerModifiers: [
                  (desc: RTCSessionDescriptionInit) =>
                    this.setVideoSendRecv(desc),
                  (desc: RTCSessionDescriptionInit) =>
                    this.fixMissingIceCredentials(desc),
                ],
              });
              console.log(
                `[SipService] Re-INVITE sent successfully to activate video`,
              );
            } catch (reInviteError) {
              console.warn(
                `[SipService] Re-INVITE failed, video may still work:`,
                reInviteError,
              );
            }

            // Apply bitrate limit after re-INVITE
            const newVideoSender = sdh.peerConnection
              .getSenders()
              .find((s) => s.track?.kind === "video");
            if (newVideoSender) {
              await this.videoQualityService.applyBitrateLimit(newVideoSender);
            }
          }
        }
      }

      // Update call state
      call.hasVideo = true;
      call.hasRemoteVideo = true; // Now safe to show remote video (was deferred in ontrack)
      call.isVideoMuted = false;
      call.hasCallerDormantVideo = false; // No longer dormant (caller path)
      call.canUpgradeToVideo = false; // Video is now active, no upgrade needed

      // Start video stats monitoring now that video is active
      // This is needed because ontrack fired before hasVideo was true, so stats didn't start there
      const sdhForStats = call.session.sessionDescriptionHandler as CustomSDH;
      if (sdhForStats?.peerConnection) {
        this.startVideoStatsMonitoring(callId, sdhForStats.peerConnection);
      }

      this.pendingVideoUpgradeCallId = null;
      this._videoUpgradeRequested.next(null);
      this.updateActiveCallsList();

      console.log(`[SipService] Video upgrade accepted for call ${callId}`);
    } catch (error) {
      console.error("[SipService] Error accepting video upgrade:", error);
      this.pendingVideoUpgradeCallId = null;
      this._videoUpgradeRequested.next(null);
      throw error;
    }
  }

  /**
   * Decline a video upgrade request - don't add local video
   * This creates asymmetric video (receive remote video but don't send local video)
   */
  async declineVideoUpgrade(callId: string): Promise<void> {
    if (this.pendingVideoUpgradeCallId !== callId) {
      throw new Error("No pending video upgrade request for this call");
    }

    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("Call not found");
    }

    try {
      console.log(
        `[SipService] Declining video upgrade for call ${callId} (audio-only mode)`,
      );

      // Simply don't add video - the re-INVITE is automatically accepted by SIP.js
      // Update call state to reflect asymmetric video
      call.hasVideo = false; // Not sending video
      call.hasRemoteVideo = true; // Will receive remote video
      call.isVideoMuted = false; // Not technically muted, just not sending

      // Start video stats monitoring for receive-only mode
      // This allows the user to see inbound video statistics even though they're not sending
      const sdh = call.session.sessionDescriptionHandler as CustomSDH;
      if (sdh?.peerConnection) {
        this.startVideoStatsMonitoring(callId, sdh.peerConnection);
      }

      this.pendingVideoUpgradeCallId = null;
      this._videoUpgradeRequested.next(null);
      this.updateActiveCallsList();

      console.log(
        `[SipService] Video upgrade declined for call ${callId}. Will receive remote video only.`,
      );
    } catch (error) {
      console.error("[SipService] Error declining video upgrade:", error);
      this.pendingVideoUpgradeCallId = null;
      this._videoUpgradeRequested.next(null);
      throw error;
    }
  }

  async holdCall(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call || call.state !== "connected") {
      throw new Error("No active call to hold");
    }

    try {
      const options = {
        sessionDescriptionHandlerModifiers: [
          (description: RTCSessionDescriptionInit) => {
            if (description.type === "offer") {
              const sdp = description.sdp!.split("\r\n");
              const modifiedSdp = sdp.map((line) => {
                if (line.includes("a=sendrecv")) {
                  return line.replace("a=sendrecv", "a=sendonly");
                } else if (line.startsWith("m=")) {
                  return line + "\r\na=sendonly";
                }
                return line;
              });
              description.sdp = modifiedSdp.join("\r\n");
            }
            return Promise.resolve(description);
          },
        ],
      };

      await call.session.invite(options);
      call.state = "held";

      // Play call held tone
      this.toneService.playCallHeldTone();

      this.updateActiveCallsList();
      // Update audio active call when call is held
      this.updateAudioActiveCall();
      // Ensure we have a valid active call ID
      this.ensureActiveCallId();
    } catch (error) {
      console.error("Error holding call:", error);
      throw error;
    }
  }

  async resumeCall(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call || call.state !== "held") {
      throw new Error("No held call to resume");
    }

    try {
      const options = {
        sessionDescriptionHandlerModifiers: [
          (description: RTCSessionDescriptionInit) => {
            if (description.type === "offer") {
              const sdp = description.sdp!.split("\r\n");
              const modifiedSdp = sdp.map((line) => {
                if (line.includes("a=sendonly")) {
                  return line.replace("a=sendonly", "a=sendrecv");
                }
                return line;
              });
              description.sdp = modifiedSdp.join("\r\n");
            }
            return Promise.resolve(description);
          },
        ],
      };

      await call.session.invite(options);
      call.state = "connected";

      // Play call resumed tone
      this.toneService.playCallResumedTone();

      // Make this the active call
      this.activeCallId.next(callId);

      this.updateActiveCallsList();
      // Update audio active call when call is resumed
      this.updateAudioActiveCall();
    } catch (error) {
      console.error("Error resuming call:", error);
      throw error;
    }
  }

  async muteAudio(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call || !call.localStream) {
      throw new Error("No active call to mute");
    }

    const shouldMute = !call.isMuted;

    // Update muted state BEFORE calling setTracksEnabled
    // (setTracksEnabled checks call.isMuted to decide whether to override)
    call.isMuted = shouldMute;

    // For the audio active call, handle muting differently
    if (callId === this.audioActiveCallId) {
      this.setTracksEnabled(call, "audio", !shouldMute);
    }

    // Play appropriate mute/unmute tone
    if (shouldMute) {
      this.toneService.playCallMutedTone();
    } else {
      this.toneService.playCallUnmutedTone();
    }

    this.updateActiveCallsList();
  }

  async muteVideo(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call || !call.localStream) {
      throw new Error("No active call to mute video");
    }

    if (call.localStream.getVideoTracks().length === 0) {
      throw new Error("No video tracks available");
    }

    const shouldMute = !call.isVideoMuted;
    this.setTracksEnabled(call, "video", !shouldMute);

    call.isVideoMuted = shouldMute;
    this.updateActiveCallsList();
  }

  async endCall(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) return;

    try {
      // Stop ringback tone if this is an outgoing call
      if (!call.isIncoming) {
        this.stopRingbackTone();
      } else if (
        call.isIncoming &&
        (call.state === "ringing" || call.state === "call-waiting")
      ) {
        // Stop ringing tone or call waiting tone if this is an incoming call being rejected
        if (call.isCallWaiting) {
          this.toneService.stopCallWaitingTone();
        } else {
          this.toneService.stopRingingTone();
        }
      }

      if (call.state === "busy") {
        // Call already rejected by remote, just clean up the busy tone
        this.toneService.stopBusyTone();
      } else if (call.state === "ringing" || call.state === "call-waiting") {
        if (call.isIncoming) {
          // For incoming calls that haven't been accepted
          await (call.session as Invitation).reject();
          this.toneService.playCallEndedTone();
        } else {
          // For outgoing calls that haven't been established
          await (call.session as Inviter).cancel();
          this.toneService.playCallEndedTone();
        }
      } else {
        // For established calls
        await call.session.bye();

        // Play call ended tone for established calls
        this.toneService.playCallEndedTone();
      }

      this.removeCall(callId);
    } catch (error) {
      console.error("Error ending call:", error);
      throw error;
    }
  }

  // blind transfer for established calls
  async blindTransfer(callId: string, targetNumber: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("No call to transfer");
    }

    try {
      const target = this.createSipUri(targetNumber);
      if (!target) {
        throw new Error("Invalid transfer target");
      }

      // set up NOTIFY handling before sending the REFER
      call.session.delegate = {
        onNotify: (notification) => {
          const subscriptionState =
            notification.request.getHeader("Subscription-State");
          if (subscriptionState?.includes("terminated")) {
            // when transfer is completed, terminate our call
            this.removeCall(callId);
          }
        },
      };

      call.state = "transferring";
      this.updateActiveCallsList();

      await call.session.refer(target);
    } catch (error) {
      console.error("Error transferring call:", error);
      throw error;
    }
  }

  // blind transfer for unanswered incoming calls
  async transferIncomingCall(
    callId: string,
    targetNumber: string,
  ): Promise<void> {
    const call = this.calls.get(callId);
    if (
      !call ||
      !call.isIncoming ||
      (call.state !== "ringing" && call.state !== "call-waiting")
    ) {
      throw new Error("No incoming call to transfer");
    }

    try {
      // first, accept the call
      await (call.session as Invitation).accept({
        sessionDescriptionHandlerOptions: {
          constraints: {
            audio: true,
            video: false,
          },
        },
      });

      // wait a brief moment for the call to establish, because SIP protocol only allows to REFER established sessions
      await new Promise((resolve) => setTimeout(resolve, 100));

      // then immediately transfer it
      await this.blindTransfer(callId, targetNumber);
    } catch (error) {
      console.error("Error transferring incoming call:", error);
      throw error;
    }
  }

  async bridgeCalls(): Promise<void> {
    // find any two calls that are in connected or held state
    const calls = Array.from(this.calls.entries()).filter(
      ([_, call]) => call.state === "connected" || call.state === "held",
    );

    if (calls.length !== 2) {
      throw new Error("Exactly two calls are required to bridge calls");
    }

    const [firstCallId, firstCall] = calls[0];
    const [secondCallId, secondCall] = calls[1];

    // store original states in case we need to revert
    const firstCallOriginalState = firstCall.state;
    const secondCallOriginalState = secondCall.state;

    try {
      // if first call is on hold, we need to take it off hold to prevent it from being bridged in held state
      if (firstCall.state === "held") {
        await this.resumeCall(firstCallId);
      }

      // update states to show bridging in progress
      firstCall.state = "transferring";
      secondCall.state = "transferring";
      this.updateActiveCallsList();

      // we need the dialog information from the second call to perform the REFER with Replaces
      const secondCallReplaces = secondCall.session.dialog?.callId;
      if (!secondCallReplaces) {
        throw new Error("Cannot bridge: second call has no dialog established");
      }

      // create the Replaces parameter
      const replacesParam = encodeURIComponent(
        `${secondCallReplaces};to-tag=${secondCall.session.dialog?.remoteTag};from-tag=${secondCall.session.dialog?.localTag}`,
      );

      // extract the target URI from the first call
      const targetUriString = firstCall.session.remoteIdentity.uri.toString();

      // create the target URI with Replaces parameter and set the Refer-To header
      const referToUri = UserAgent.makeURI(
        `${targetUriString}?Replaces=${replacesParam}`,
      );
      if (!referToUri) {
        throw new Error("Failed to create URI for bridge operation");
      }

      // set up NOTIFY handler to track transfer completion
      secondCall.session.delegate = {
        onNotify: (notification) => {
          const subscriptionState =
            notification.request.getHeader("Subscription-State");
          if (subscriptionState?.includes("terminated")) {
            // when transfer is completed, remove both calls
            this.removeCall(firstCallId);
            this.removeCall(secondCallId);
          }
        },
      };

      // perform the REFER
      await secondCall.session.refer(referToUri);
      // (both calls will be removed when the NOTIFY with terminated state is received)
    } catch (error) {
      console.error("Error bridging calls:", error);
      // revert to original states in case of error
      firstCall.state = firstCallOriginalState;
      secondCall.state = secondCallOriginalState;
      this.updateActiveCallsList();
      throw error;
    }
  }

  async createConference(): Promise<void> {
    // get all active and held calls
    const calls = Array.from(this.calls.entries()).filter(
      ([_, call]) => call.state === "connected" || call.state === "held",
    );

    if (calls.length < 2) {
      throw new Error("At least two calls are required to create a conference");
    }

    try {
      // sort calls so that the active call is first (if any)
      calls.sort(([_, a], [__, b]) =>
        a.state === "connected" ? -1 : b.state === "connected" ? 1 : 0,
      );

      // the first call will be our "conference host" call
      const [hostCallId, hostCall] = calls[0];

      // check if all calls are on hold
      const allCallsOnHold = calls.every(([_, call]) => call.state === "held");
      // if all calls are on hold, take the host call off hold
      if (allCallsOnHold) {
        await this.resumeCall(hostCallId);
        hostCall.state = "connected";
      }

      // update the host call to be a conference call
      hostCall.isConference = true;
      hostCall.state = "conference";

      // Format participant list with call name information when available
      hostCall.participants = calls.map(([_, call]) => {
        if (call.name && call.name !== call.number) {
          return `${call.name} (${call.number})`;
        }
        return call.number || "Unknown";
      });

      // for each other call, we need to perform a bridge (REFER with Replaces)
      for (let i = 1; i < calls.length; i++) {
        const [callId, call] = calls[i];

        // update state to show transferring
        call.state = "transferring";
        this.updateActiveCallsList();

        // we need the dialog info from this call
        const callReplaces = call.session.dialog?.callId;
        if (!callReplaces) {
          console.error(
            `Cannot add call ${callId} to conference: no dialog established`,
          );
          continue;
        }

        // create the Replaces parameter
        const replacesParam = encodeURIComponent(
          `${callReplaces};to-tag=${call.session.dialog?.remoteTag};from-tag=${call.session.dialog?.localTag}`,
        );

        // extract the target URI from the conference host call
        const targetUriString = hostCall.session.remoteIdentity.uri.toString();

        // create the target URI with Replaces parameter and set the Refer-To header
        const referToUri = UserAgent.makeURI(
          `${targetUriString}?Replaces=${replacesParam}`,
        );
        if (!referToUri) {
          console.error(`Failed to create URI for conference operation`);
          continue;
        }

        // perform the REFER
        await call.session.refer(referToUri);
        // remove this call from our list
        this.removeCall(callId);
      }

      // set the host call as the active call
      this.activeCallId.next(hostCallId);
      this.updateActiveCallsList();
    } catch (error) {
      console.error("Error creating conference:", error);
      throw error;
    }
  }

  async sendDTMF(callId: string, tone: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("No call found for sending DTMF");
    }

    try {
      const sdh = call.session.sessionDescriptionHandler as
        | CustomSDH
        | undefined;
      if (sdh?.peerConnection) {
        const audioSender = sdh.peerConnection
          .getSenders()
          .find((s) => s.track?.kind === "audio");
        if (audioSender?.dtmf?.canInsertDTMF) {
          audioSender.dtmf.insertDTMF(tone, 100, 70);
          return;
        }
      }

      // Fallback to SIP INFO if in-band DTMF is not available
      const options = {
        requestOptions: {
          body: {
            contentDisposition: "render",
            contentType: "application/dtmf-relay",
            content: `Signal=${tone}\r\nDuration=100`,
          },
        },
      };

      await call.session.info(options);
    } catch (error) {
      console.error("Error sending DTMF:", error);
      throw error;
    }
  }

  private findCallIdBySession(session: Session): string | null {
    for (const [id, call] of this.calls.entries()) {
      if (call.session === session) {
        return id;
      }
    }
    return null;
  }

  /**
   * Updates which call should have active audio (separate from UI active call)
   * Connected calls take priority for audio, even if they're not the UI active call
   */
  private updateAudioActiveCall(): void {
    // Find the best call for audio (prioritize connected calls)
    const connectedCalls = Array.from(this.calls.entries())
      .filter(([_, call]) => call.state === "connected")
      .sort(([, a], [, b]) => {
        // If one is a conference and the other isn't, prefer the conference
        if (a.isConference && !b.isConference) return -1;
        if (!a.isConference && b.isConference) return 1;
        return 0;
      });

    const previousAudioActiveCallId = this.audioActiveCallId;

    if (connectedCalls.length > 0) {
      this.audioActiveCallId = connectedCalls[0][0];
    } else {
      this.audioActiveCallId = null;
    }

    // If audio active call changed, update audio tracks
    if (previousAudioActiveCallId !== this.audioActiveCallId) {
      console.log(
        `Audio active call changed from ${previousAudioActiveCallId} to ${this.audioActiveCallId}`,
      );
      this.updateAllCallAudioStates();
    } else {
      console.log(`Audio active call remains: ${this.audioActiveCallId}`);
    }
  }

  /**
   * Updates audio state for all calls based on current audio active call
   */
  private updateAllCallAudioStates(): void {
    for (const [callId, call] of this.calls.entries()) {
      if (call.state === "connected") {
        const shouldHaveAudio = callId === this.audioActiveCallId;
        // Enable audio for the audio active call, unless it's explicitly muted
        this.setTracksEnabled(call, "audio", shouldHaveAudio && !call.isMuted);
      }
    }
  }
  private ensureActiveCallId(): void {
    const currentActiveCallId = this.activeCallId.value;
    const callIds = Array.from(this.calls.keys());

    // If current active call ID is valid, keep it
    if (currentActiveCallId && this.calls.has(currentActiveCallId)) {
      return;
    }

    // If no valid active call ID but we have calls, pick the best one
    if (callIds.length > 0) {
      // Priority order: call-waiting (needs immediate user interaction) > ringing (incoming) > connected > conference > ringing (outgoing) > held > transferring > idle
      const prioritizedCalls = Array.from(this.calls.entries()).sort(
        ([, a], [, b]) => {
          const getPriority = (call: CallInfo): number => {
            // Call waiting always takes highest priority because it needs immediate user interaction
            if (call.state === "call-waiting") return 0;
            // Incoming ringing calls also need immediate attention
            if (call.state === "ringing" && call.isIncoming) return 1;
            // Connected calls are next priority
            if (call.state === "connected") return 2;
            // Conference calls
            if (call.state === "conference") return 3;
            // Outgoing ringing calls
            if (call.state === "ringing" && !call.isIncoming) return 4;
            // Held calls
            if (call.state === "held") return 5;
            // Transferring calls
            if (call.state === "transferring") return 6;
            // Idle calls (lowest priority)
            if (call.state === "idle") return 7;
            // Fallback
            return 99;
          };

          return getPriority(a) - getPriority(b);
        },
      );

      const bestCallId = prioritizedCalls[0][0];
      console.log(
        `Setting active call ID to best available call: ${bestCallId} (state: ${prioritizedCalls[0][1].state})`,
      );
      this.activeCallId.next(bestCallId);
    } else {
      // No calls, set to null
      this.activeCallId.next(null);
    }
  }

  private removeCall(callId: string): void {
    const call = this.calls.get(callId);
    if (call) {
      // Clear busy call timeout if set
      const busyTimeout = this.busyCallTimeouts.get(callId);
      if (busyTimeout) {
        clearTimeout(busyTimeout);
        this.busyCallTimeouts.delete(callId);
        this.toneService.stopBusyTone();
      }

      // Stop video stats monitoring if active
      this.stopVideoStatsMonitoring(callId);

      if (call.localStream) {
        call.localStream.getTracks().forEach((track) => track.stop());
      }
      this.calls.delete(callId);

      // Update active calls list first
      this.updateActiveCallsList();

      // Update audio active call if the removed call was the audio active call
      if (callId === this.audioActiveCallId) {
        this.updateAudioActiveCall();
      }

      // Clear pending video upgrade request if it was for this call
      if (this.pendingVideoUpgradeCallId === callId) {
        this.pendingVideoUpgradeCallId = null;
        this._videoUpgradeRequested.next(null);
      }

      // Handle active call ID update
      if (this.activeCallId.value === callId) {
        // The removed call was the active call, need to find a new one
        const remainingCallIds = Array.from(this.calls.keys());
        if (remainingCallIds.length > 0) {
          // Find the best replacement call using the same priority logic as ensureActiveCallId
          const remainingCalls = Array.from(this.calls.entries()).sort(
            ([, a], [, b]) => {
              const getPriority = (call: CallInfo): number => {
                // Call waiting always takes highest priority because it needs immediate user interaction
                if (call.state === "call-waiting") return 0;
                // Incoming ringing calls also need immediate attention
                if (call.state === "ringing" && call.isIncoming) return 1;
                // Connected calls are next priority
                if (call.state === "connected") return 2;
                // Conference calls
                if (call.state === "conference") return 3;
                // Outgoing ringing calls
                if (call.state === "ringing" && !call.isIncoming) return 4;
                // Held calls
                if (call.state === "held") return 5;
                // Transferring calls
                if (call.state === "transferring") return 6;
                // Idle calls (lowest priority)
                if (call.state === "idle") return 7;
                // Fallback
                return 99;
              };

              return getPriority(a) - getPriority(b);
            },
          );

          const newActiveCallId = remainingCalls[0][0];
          console.log(
            `Removed active call ${callId}, setting new active call to: ${newActiveCallId} (state: ${remainingCalls[0][1].state})`,
          );
          this.activeCallId.next(newActiveCallId);
        } else {
          console.log(`Removed last call ${callId}, no active call remaining`);
          this.activeCallId.next(null);
        }
      }

      // Ensure we have a valid active call ID after removal
      this.ensureActiveCallId();
    }
  }

  private updateActiveCallsList(): void {
    const activeCallsList = Array.from(this.calls.entries()).map(
      ([id, call]) => ({
        id,
        ...call,
      }),
    );
    this.activeCalls.next(activeCallsList);
  }

  private checkInitialized(): void {
    if (!this.initialized) {
      throw new Error(
        "SIP service is not initialized. Call initialize() first.",
      );
    }
  }

  // observable getters
  getActiveCalls() {
    return this.activeCalls.asObservable();
  }

  getActiveCallId() {
    return this.activeCallId.asObservable();
  }

  // Get the ID of the call that should have active audio (for hearing remote party)
  getAudioActiveCallId(): string | null {
    return this.audioActiveCallId;
  }

  // Methods for updating audio devices on active calls
  async updateAudioDevices(): Promise<void> {
    // Update all active calls with the new devices
    for (const [callId, _] of this.calls.entries()) {
      await this.updateCallAudioInput(callId);
      await this.updateCallAudioOutput(callId);
    }
  }

  /**
   * Detect video capabilities (camera, codecs, resolutions)
   */
  async detectVideoCapabilities(): Promise<VideoCapability> {
    return await this.videoDeviceService.detectVideoCapabilities();
  }

  /**
   * Toggle video mute for a call
   */
  async toggleVideoMute(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call || !call.localStream) {
      throw new Error("No active call to toggle video");
    }

    if (call.localStream.getVideoTracks().length === 0) {
      throw new Error("No video tracks available");
    }

    const shouldMute = !call.isVideoMuted;
    this.setTracksEnabled(call, "video", !shouldMute);

    call.isVideoMuted = shouldMute;
    this.updateActiveCallsList();

    console.log(`Video ${shouldMute ? "muted" : "unmuted"} for call ${callId}`);
  }

  /**
   * Upgrade an audio-only call to include video
   * Video quality is always 'auto'
   */
  async upgradeToVideo(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("Call not found");
    }

    if (call.hasVideo) {
      console.warn("Call already has video");
      return;
    }

    if (call.state !== "connected") {
      throw new Error("Can only upgrade connected calls to video");
    }

    // Check if there's already a renegotiation in progress for this call
    const existingRenegotiation = this.ongoingRenegotiations.get(callId);
    if (existingRenegotiation) {
      console.warn(
        `Renegotiation already in progress for call ${callId}, waiting...`,
      );
      try {
        await existingRenegotiation;
      } catch (error) {
        console.warn("Previous renegotiation failed:", error);
      }
    }

    // Create a promise for this renegotiation
    const renegotiationPromise = this.performUpgradeToVideo(callId);
    this.ongoingRenegotiations.set(callId, renegotiationPromise);

    try {
      await renegotiationPromise;
    } finally {
      this.ongoingRenegotiations.delete(callId);
    }
  }

  private async performUpgradeToVideo(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("Call not found");
    }

    try {
      console.log(`[upgradeToVideo] Upgrading call ${callId} to video`);

      // Check if peer connection is in correct state
      if (call.session.sessionDescriptionHandler) {
        const sdh = call.session.sessionDescriptionHandler as CustomSDH;
        if (sdh.peerConnection) {
          const signalingState = sdh.peerConnection.signalingState;

          if (signalingState !== "stable") {
            console.warn(
              `PeerConnection in ${signalingState} state, rolling back before upgrade`,
            );

            // Rollback to stable state
            try {
              await sdh.peerConnection.setLocalDescription({
                type: "rollback",
              } as RTCSessionDescriptionInit);
              console.log("Rolled back to stable state");
            } catch (rollbackError) {
              console.error("Error rolling back:", rollbackError);
              throw new Error(
                "Cannot upgrade: PeerConnection not in stable state",
              );
            }
          }
        }
      }

      // Get video stream (auto quality - browser handles adaptive scaling)
      const videoStream = await this.videoDeviceService.getVideoStream("auto");
      const videoTrack = videoStream.getVideoTracks()[0];

      if (!videoTrack) {
        throw new Error("Failed to get video track");
      }

      // Replace video track in RTCPeerConnection (maintains m-line order)
      if (call.session.sessionDescriptionHandler) {
        const sdh = call.session.sessionDescriptionHandler as CustomSDH;
        if (sdh.peerConnection) {
          const senders = sdh.peerConnection.getSenders();
          const transceivers = sdh.peerConnection.getTransceivers();

          // CRITICAL: Find the NEGOTIATED video transceiver (one with a mid assigned).
          // When both parties have dormant video, there are multiple video transceivers.
          // Only the one from the original call negotiation has a mid and will be accepted.
          // The callee's own dormant video transceiver may be REJECTED (port 0 in answer).
          // We must use the negotiated transceiver to send video.
          const negotiatedVideoTransceiver = transceivers.find(
            (t) =>
              t.mid !== null &&
              (t.receiver?.track?.kind === "video" ||
                t.sender?.track?.kind === "video"),
          );

          let videoSender: RTCRtpSender | undefined;
          let videoTransceiverToUse: RTCRtpTransceiver | undefined;

          if (negotiatedVideoTransceiver) {
            // Use the negotiated transceiver's sender - this is the one that will be accepted
            videoSender = negotiatedVideoTransceiver.sender;
            videoTransceiverToUse = negotiatedVideoTransceiver;
            console.log(
              `[upgradeToVideo] Found NEGOTIATED video transceiver (mid=${negotiatedVideoTransceiver.mid}), using its sender`,
            );
          } else {
            // Fallback: Find any video sender (for non-dormant video scenarios)
            videoSender = senders.find((sender) => {
              if (sender.track?.kind === "video") {
                return true;
              }
              if (sender.track === null) {
                return sender.dtmf === null;
              }
              return false;
            });
            // Find the transceiver for this sender
            videoTransceiverToUse = transceivers.find(
              (t) => t.sender === videoSender,
            );
            console.log(
              `[upgradeToVideo] No negotiated transceiver found, using fallback video sender`,
            );
          }

          console.log(`Video sender ${videoSender ? "FOUND" : "NOT FOUND"}`, {
            totalSenders: senders.length,
            totalTransceivers: transceivers.length,
            negotiatedMid: negotiatedVideoTransceiver?.mid,
            senderDetails: senders.map((s) => ({
              trackKind: s.track?.kind,
              trackEnabled: s.track?.enabled,
              trackReadyState: s.track?.readyState,
              hasDtmf: s.dtmf !== null,
            })),
            transceiverDetails: transceivers.map((t) => ({
              mid: t.mid,
              direction: t.direction,
              currentDirection: t.currentDirection,
              senderTrackKind: t.sender?.track?.kind,
              receiverTrackKind: t.receiver?.track?.kind,
            })),
          });

          let senderForBitrateLimit: RTCRtpSender | null = null;

          if (videoSender) {
            // Replace existing track (maintains m-line)
            console.log("Replacing existing video track");

            // Enable the new track BEFORE replacing (critical for SDP generation)
            videoTrack.enabled = true;

            // Replace the track
            await videoSender.replaceTrack(videoTrack);
            senderForBitrateLimit = videoSender;

            // Update local stream
            if (call.localStream) {
              // Remove and stop old tracks (dummy black track from downgrade, or previous camera)
              const oldVideoTracks = call.localStream.getVideoTracks();
              oldVideoTracks.forEach((track) => {
                call.localStream?.removeTrack(track);
                track.stop(); // Safe to stop - sender maintained via replaceTrack()
              });
              // Add new active track
              call.localStream.addTrack(videoTrack);
            }

            console.log(
              "[upgradeToVideo] Successfully replaced dummy/old track with new camera track",
            );

            // CRITICAL: Set transceiver direction to sendrecv BEFORE creating the re-INVITE SDP.
            // The sender may have been disabled for dormant video (callee or caller).
            // WebRTC uses transceiver.direction to determine actual RTP flow.
            if (
              videoTransceiverToUse &&
              videoTransceiverToUse.direction !== "sendrecv"
            ) {
              console.log(
                `[upgradeToVideo] Setting video transceiver (mid=${videoTransceiverToUse.mid}) direction from '${videoTransceiverToUse.direction}' to 'sendrecv'`,
              );
              videoTransceiverToUse.direction = "sendrecv";
            }
          } else {
            // No existing video sender - add new one (initial video call)
            // This path is hit when dormant video transceiver was killed by Asterisk re-INVITE with m=video 0
            console.log("Adding new video track (first time)");
            if (call.localStream) {
              // Remove and stop old video tracks (dormant black track that may still be in localStream)
              // This is needed because the dormant track may not have been removed when the transceiver was killed
              const oldVideoTracks = call.localStream.getVideoTracks();
              if (oldVideoTracks.length > 0) {
                console.log(
                  `[upgradeToVideo] Removing ${oldVideoTracks.length} old video track(s) from localStream`,
                );
                oldVideoTracks.forEach((track) => {
                  call.localStream?.removeTrack(track);
                  track.stop();
                });
              }
              call.localStream.addTrack(videoTrack);
            } else {
              call.localStream = videoStream;
            }
            videoTrack.enabled = true;
            senderForBitrateLimit = sdh.peerConnection.addTrack(
              videoTrack,
              call.localStream,
            );
          }

          // Re-negotiate with video
          // When there's an existing video sender (from dormant video on caller OR callee),
          // we need to use setVideoSendRecv modifier to change direction from inactive to sendrecv.
          // For new video tracks (addTrack path), we can use empty modifiers.
          // IMPORTANT: The modifiers array is used for BOTH offer and answer processing.
          // - setVideoSendRecv: Changes a=inactive to a=sendrecv (needed for offer)
          // - fixMissingIceCredentials: Copies ICE from audio to video if missing (needed for answer)
          const hasExistingVideoSender = videoSender !== undefined;

          // Build modifiers array - always include ICE fix for interop with non-WebRTC endpoints
          const modifiers: Array<
            (
              desc: RTCSessionDescriptionInit,
            ) => Promise<RTCSessionDescriptionInit>
          > = [
            // ICE credentials fix for answers from SDES-based clients via Asterisk
            (desc: RTCSessionDescriptionInit) =>
              this.fixMissingIceCredentials(desc),
          ];

          // Add video direction modifier if there's an existing sender
          if (hasExistingVideoSender) {
            modifiers.unshift((desc: RTCSessionDescriptionInit) =>
              this.setVideoSendRecv(desc),
            );
          }

          const options = {
            sessionDescriptionHandlerOptions: {
              constraints: {
                audio: true,
                video: true,
              },
            },
            sessionDescriptionHandlerModifiers: modifiers,
          };

          try {
            // Enable the video sender BEFORE re-INVITE when there's an existing sender
            // This is needed for both caller (fast path) and callee (dormant video) upgrades
            // IMPORTANT: Enable the SPECIFIC sender we found, not just any video sender
            if (hasExistingVideoSender && videoSender) {
              try {
                const params = videoSender.getParameters();
                if (!params.encodings || params.encodings.length === 0) {
                  params.encodings = [{}];
                }
                params.encodings[0].active = true;
                await videoSender.setParameters(params);
                console.log(
                  "[upgradeToVideo] Video sender enabled (active=true) on specific sender",
                );
              } catch (enableError) {
                console.error(
                  "[upgradeToVideo] Error enabling video sender:",
                  enableError,
                );
              }
            }

            await call.session.invite(options);

            // CRITICAL: Re-enable the video sender AFTER renegotiation completes
            // SDP negotiation can reset encoding parameters, including active=false
            if (hasExistingVideoSender && videoSender) {
              try {
                const paramsAfter = videoSender.getParameters();
                if (
                  !paramsAfter.encodings ||
                  paramsAfter.encodings.length === 0
                ) {
                  paramsAfter.encodings = [{}];
                }
                // Only re-enable if it was reset
                if (paramsAfter.encodings[0].active !== true) {
                  console.log(
                    "[upgradeToVideo] Encoding active was reset by negotiation, re-enabling",
                  );
                  paramsAfter.encodings[0].active = true;
                  await videoSender.setParameters(paramsAfter);
                }
              } catch (reEnableError) {
                console.error(
                  "[upgradeToVideo] Error re-enabling sender after negotiation:",
                  reEnableError,
                );
              }
            }

            // Apply bitrate limit AFTER renegotiation completes
            // (SDP negotiation can reset encoding parameters)
            if (senderForBitrateLimit) {
              await this.videoQualityService.applyBitrateLimit(
                senderForBitrateLimit,
              );
            }

            // Update call state
            call.hasVideo = true;
            call.isVideoMuted = false;
            call.videoQuality = "auto";
            call.hasCallerDormantVideo = false; // No longer dormant - video is active
            call.canUpgradeToVideo = false; // Video is now active, no upgrade needed

            this.updateActiveCallsList();
            console.log(
              `Successfully upgraded call ${callId} to video (existing sender: ${hasExistingVideoSender})`,
            );
          } catch (inviteError) {
            console.error("Re-INVITE failed during upgrade:", inviteError);

            // Rollback if re-INVITE failed
            if (sdh.peerConnection.signalingState === "have-local-offer") {
              console.log("Rolling back signaling state after failed upgrade");
              await sdh.peerConnection.setLocalDescription({
                type: "rollback",
              } as RTCSessionDescriptionInit);
            }

            // If we enabled an existing sender, disable it again on failure
            if (hasExistingVideoSender && videoSender) {
              try {
                const params = videoSender.getParameters();
                if (!params.encodings || params.encodings.length === 0) {
                  params.encodings = [{}];
                }
                params.encodings[0].active = false;
                await videoSender.setParameters(params);
                console.log("[upgradeToVideo] Rollback: video sender disabled");
              } catch (disableError) {
                console.warn(
                  "[upgradeToVideo] Rollback: failed to disable sender:",
                  disableError,
                );
              }
            }

            // Stop the video track and disable it (but keep sender for m-line consistency)
            videoTrack.enabled = false;
            videoTrack.stop();

            throw inviteError;
          }
        }
      }
    } catch (error) {
      console.error("Error upgrading call to video:", error);
      throw error;
    }
  }

  /**
   * Downgrade a video call to audio-only
   */
  async downgradeToAudio(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("Call not found");
    }

    if (!call.hasVideo) {
      console.warn("Call does not have video");
      return;
    }

    // Check if there's already a renegotiation in progress for this call
    const existingRenegotiation = this.ongoingRenegotiations.get(callId);
    if (existingRenegotiation) {
      console.warn(
        `Renegotiation already in progress for call ${callId}, waiting...`,
      );
      try {
        await existingRenegotiation;
      } catch (error) {
        console.warn("Previous renegotiation failed:", error);
      }
    }

    // Create a promise for this renegotiation
    const renegotiationPromise = this.performDowngradeToAudio(callId);
    this.ongoingRenegotiations.set(callId, renegotiationPromise);

    try {
      await renegotiationPromise;
    } finally {
      this.ongoingRenegotiations.delete(callId);
    }
  }

  private async performDowngradeToAudio(callId: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call) {
      throw new Error("Call not found");
    }

    try {
      console.log(`Downgrading call ${callId} to audio-only (dormant video)`);

      // Replace real video track with dormant dummy track to maintain sender
      // This is CRITICAL: We need to keep the sender alive in the peer connection
      // to maintain m-line order for subsequent upgrade
      if (call.session.sessionDescriptionHandler) {
        const sdh = call.session.sessionDescriptionHandler as CustomSDH;
        if (sdh.peerConnection) {
          const senders = sdh.peerConnection.getSenders();
          const videoSender = senders.find(
            (sender) => sender.track?.kind === "video",
          );

          if (videoSender && videoSender.track) {
            console.log(
              "[downgradeToAudio] Replacing real video track with dormant dummy track",
            );

            // Store the real track to stop it later
            const realVideoTrack = videoSender.track;

            // Create dormant dummy track (smaller, lower framerate)
            const dormantTrack = this.createDormantVideoTrack();

            // Replace real track with dummy track (keeps sender alive!)
            await videoSender.replaceTrack(dormantTrack);

            // Update local stream
            if (call.localStream) {
              const videoTracks = call.localStream.getVideoTracks();
              videoTracks.forEach((track) =>
                call.localStream?.removeTrack(track),
              );
              call.localStream.addTrack(dormantTrack);
            }

            // Now safe to stop the real track (sender has dummy track)
            realVideoTrack.stop();

            console.log(
              "[downgradeToAudio] Successfully replaced real track with dormant dummy track",
            );
            console.log(
              "[downgradeToAudio] Video sender still present - m-line preserved",
            );

            // Disable the video sender to ensure zero bandwidth
            await this.disableVideoSender(call.session);

            // Send re-INVITE with a=inactive for video
            // This tells remote we're not sending/receiving video anymore
            const options = {
              sessionDescriptionHandlerOptions: {
                constraints: {
                  audio: true,
                  video: true, // Keep video m-line
                },
              },
              sessionDescriptionHandlerModifiers: [
                (desc: RTCSessionDescriptionInit) =>
                  this.setVideoInactive(desc),
              ],
            };

            try {
              await call.session.invite(options);
              console.log(
                "[downgradeToAudio] Re-INVITE sent with a=inactive for video",
              );
            } catch (inviteError) {
              console.warn(
                "[downgradeToAudio] Re-INVITE failed, but local state updated:",
                inviteError,
              );
              // Continue anyway - local state is already correct
            }
          }
        }
      }

      // Update call state
      call.hasVideo = false;
      call.isVideoMuted = false;
      call.hasRemoteVideo = false;
      call.hasCallerDormantVideo = true; // Now dormant - can upgrade without full renegotiation
      call.canUpgradeToVideo = true; // Can upgrade again

      this.updateActiveCallsList();
      console.log(
        `Successfully downgraded call ${callId} to audio-only (dormant video active)`,
      );
    } catch (error) {
      console.error("Error downgrading call to audio-only:", error);
      throw error;
    }
  }

  /**
   * Create a black video track for use as a dummy track during video downgrade
   * This keeps the video sender alive in the peer connection to maintain m-line order
   */
  private createBlackVideoTrack(
    width = 640,
    height = 480,
    frameRate = 1,
  ): MediaStreamTrack {
    console.log("[createBlackVideoTrack] Creating black dummy video track");

    // Create a canvas and fill it with black
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;

    const ctx = canvas.getContext("2d");
    if (ctx) {
      ctx.fillStyle = "black";
      ctx.fillRect(0, 0, width, height);
    }

    // Capture stream from canvas at 1 fps (minimal overhead)
    const stream = canvas.captureStream(frameRate);
    const track = stream.getVideoTracks()[0];

    if (!track) {
      throw new Error("Failed to create black video track");
    }

    console.log("[createBlackVideoTrack] Black dummy track created");
    return track;
  }

  /**
   * Create a minimal dummy video track for dormant video (no camera access needed)
   * Uses small resolution and 1fps to minimize any resource usage
   */
  private createDormantVideoTrack(): MediaStreamTrack {
    return this.createBlackVideoTrack(160, 120, 1);
  }

  /**
   * SDP modifier to set video direction to inactive
   * This signals to the remote that we have video capability but are not sending/receiving yet
   */
  private setVideoInactive(
    description: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    if (!description.sdp) return Promise.resolve(description);

    const lines = description.sdp.split("\r\n");
    const result: string[] = [];
    let inVideoSection = false;
    let hasReplacedDirection = false;

    for (const line of lines) {
      if (line.startsWith("m=video")) {
        inVideoSection = true;
        hasReplacedDirection = false;
        result.push(line);
      } else if (line.startsWith("m=")) {
        inVideoSection = false;
        result.push(line);
      } else if (
        inVideoSection &&
        (line === "a=sendrecv" ||
          line === "a=sendonly" ||
          line === "a=recvonly")
      ) {
        // Replace direction attribute with inactive
        result.push("a=inactive");
        hasReplacedDirection = true;
      } else {
        result.push(line);
      }
    }

    const modifiedSdp = result.join("\r\n");
    console.log(
      "[setVideoInactive] Modified SDP to set video direction to inactive",
    );
    return Promise.resolve({ ...description, sdp: modifiedSdp });
  }

  /**
   * SDP modifier to set video direction to sendrecv (for activating dormant video)
   */
  private setVideoSendRecv(
    description: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    if (!description.sdp) return Promise.resolve(description);

    const lines = description.sdp.split("\r\n");
    const result: string[] = [];
    let inVideoSection = false;

    for (const line of lines) {
      if (line.startsWith("m=video")) {
        inVideoSection = true;
        result.push(line);
      } else if (line.startsWith("m=")) {
        inVideoSection = false;
        result.push(line);
      } else if (
        inVideoSection &&
        (line === "a=inactive" ||
          line === "a=sendonly" ||
          line === "a=recvonly")
      ) {
        // Replace direction attribute with sendrecv
        result.push("a=sendrecv");
      } else {
        result.push(line);
      }
    }

    const modifiedSdp = result.join("\r\n");
    console.log(
      "[setVideoSendRecv] Modified SDP to set video direction to sendrecv",
    );
    return Promise.resolve({ ...description, sdp: modifiedSdp });
  }

  /**
   * SDP modifier to fix missing ICE credentials in video m-line.
   *
   * This is needed for interoperability with non-WebRTC endpoints (like SDES-based SIP clients)
   * that are proxied through Asterisk. When Asterisk adds a new video m-line in the SDP answer,
   * it may not include ICE credentials (ice-ufrag, ice-pwd) for that m-line, causing WebRTC
   * to reject the SDP with "Called with SDP without ice-ufrag and ice-pwd."
   *
   * This modifier:
   * 1. Copies ice-ufrag and ice-pwd from audio to video if missing
   * 2. Generates a video candidate using the audio candidate template but with the video port
   *    (extracted from the m=video line)
   */
  private fixMissingIceCredentials(
    description: RTCSessionDescriptionInit,
  ): Promise<RTCSessionDescriptionInit> {
    if (!description.sdp) return Promise.resolve(description);

    const lines = description.sdp.split("\r\n");

    // First pass: Extract ICE credentials and first candidate from audio section
    let audioIceUfrag: string | null = null;
    let audioIcePwd: string | null = null;
    let audioCandidate: string | null = null;
    let inAudioSection = false;

    for (const line of lines) {
      if (line.startsWith("m=audio")) {
        inAudioSection = true;
      } else if (line.startsWith("m=")) {
        inAudioSection = false;
      }

      if (inAudioSection) {
        if (line.startsWith("a=ice-ufrag:")) {
          audioIceUfrag = line;
        } else if (line.startsWith("a=ice-pwd:")) {
          audioIcePwd = line;
        } else if (line.startsWith("a=candidate:") && !audioCandidate) {
          // Only take the first candidate as a template
          audioCandidate = line;
        }
      }
    }

    // If no audio ICE credentials found, nothing we can do
    if (!audioIceUfrag || !audioIcePwd) {
      console.log(
        "[fixMissingIceCredentials] No audio ICE credentials found, cannot fix video section",
      );
      return Promise.resolve(description);
    }

    // Second pass: Check video section state and extract video port
    let inVideoSection = false;
    let videoHasIceUfrag = false;
    let videoHasIcePwd = false;
    let videoHasCandidate = false;
    let videoPort: string | null = null;

    for (const line of lines) {
      if (line.startsWith("m=video")) {
        inVideoSection = true;
        videoHasIceUfrag = false;
        videoHasIcePwd = false;
        videoHasCandidate = false;
        // Extract video port: m=video <port> ...
        const portMatch = line.match(/^m=video\s+(\d+)/);
        if (portMatch) {
          videoPort = portMatch[1];
        }
      } else if (line.startsWith("m=") && inVideoSection) {
        // End of video section
        break;
      }

      if (inVideoSection) {
        if (line.startsWith("a=ice-ufrag:")) {
          videoHasIceUfrag = true;
        } else if (line.startsWith("a=ice-pwd:")) {
          videoHasIcePwd = true;
        } else if (line.startsWith("a=candidate:")) {
          videoHasCandidate = true;
        }
      }
    }

    // If video has ICE credentials and candidate, no fix needed
    if (videoHasIceUfrag && videoHasIcePwd && videoHasCandidate) {
      return Promise.resolve(description);
    }

    // Generate video candidate from audio candidate template with video port
    let videoCandidate: string | null = null;
    if (audioCandidate && videoPort && videoPort !== "0") {
      // Audio candidate format: a=candidate:{foundation} {component} {transport} {priority} {ip} {port} typ {type} ...
      // We need to replace the port with videoPort
      // Example: a=candidate:Hd4330b85 1 UDP 2130706431 212.51.11.133 15348 typ host
      const candidateMatch = audioCandidate.match(
        /^(a=candidate:\S+\s+\d+\s+\S+\s+\d+\s+[\d.]+)\s+(\d+)\s+(typ\s+.*)$/,
      );
      if (candidateMatch) {
        videoCandidate = `${candidateMatch[1]} ${videoPort} ${candidateMatch[3]}`;
      }
    }

    // Third pass: Inject missing ICE credentials and candidate into video section
    const result: string[] = [];
    inVideoSection = false;
    let injectedIce = false;

    for (let i = 0; i < lines.length; i++) {
      const line = lines[i];

      if (line.startsWith("m=video")) {
        inVideoSection = true;
        injectedIce = false;
        result.push(line);
      } else if (line.startsWith("m=") && inVideoSection) {
        // Leaving video section
        inVideoSection = false;
        result.push(line);
      } else if (inVideoSection && !injectedIce && line.startsWith("a=")) {
        // First a= line in video section - inject ICE credentials and candidate before it
        if (!videoHasIceUfrag) {
          result.push(audioIceUfrag);
        }
        if (!videoHasIcePwd) {
          result.push(audioIcePwd);
        }
        // Inject generated video candidate if video doesn't have one
        if (!videoHasCandidate && videoCandidate) {
          result.push(videoCandidate);
        }
        injectedIce = true;
        result.push(line);
      } else {
        result.push(line);
      }
    }

    const modifiedSdp = result.join("\r\n");
    console.log(
      "[fixMissingIceCredentials] Injected ICE credentials into video section",
    );

    return Promise.resolve({ ...description, sdp: modifiedSdp });
  }

  /**
   * Check if remote accepted video in SDP (port is not 0)
   */
  private checkVideoAcceptedInSDP(sdp: string): boolean {
    if (!sdp) return false;

    const lines = sdp.split("\r\n");
    for (const line of lines) {
      if (line.startsWith("m=video")) {
        const parts = line.split(" ");
        if (parts.length >= 2) {
          const port = parts[1];
          // Port 0 means video was rejected
          if (port !== "0") {
            return true;
          }
        }
      }
    }
    return false;
  }

  /**
   * Disable video sender (set active=false) for dormant video state
   * This ensures zero bandwidth is used while keeping the m-line
   */
  private async disableVideoSender(session: Session): Promise<void> {
    if (!session.sessionDescriptionHandler) return;

    const sdh = session.sessionDescriptionHandler as CustomSDH;
    if (!sdh.peerConnection) return;

    const senders = sdh.peerConnection.getSenders();
    const videoSender = senders.find((s) => s.track?.kind === "video");

    if (videoSender) {
      try {
        const params = videoSender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        params.encodings[0].active = false;
        await videoSender.setParameters(params);
        console.log(
          "[disableVideoSender] Video sender disabled (active=false)",
        );
      } catch (error) {
        console.error(
          "[disableVideoSender] Error disabling video sender:",
          error,
        );
      }
    }
  }

  /**
   * Enable video sender (set active=true) for activating dormant video
   */
  private async enableVideoSender(
    session: Session,
  ): Promise<RTCRtpSender | null> {
    if (!session.sessionDescriptionHandler) return null;

    const sdh = session.sessionDescriptionHandler as CustomSDH;
    if (!sdh.peerConnection) return null;

    const senders = sdh.peerConnection.getSenders();
    const videoSender = senders.find((s) => s.track?.kind === "video");

    if (videoSender) {
      try {
        const params = videoSender.getParameters();
        if (!params.encodings || params.encodings.length === 0) {
          params.encodings = [{}];
        }
        params.encodings[0].active = true;
        await videoSender.setParameters(params);
        console.log("[enableVideoSender] Video sender enabled (active=true)");
        return videoSender;
      } catch (error) {
        console.error(
          "[enableVideoSender] Error enabling video sender:",
          error,
        );
      }
    }
    return null;
  }

  /**
   * Update video input device for an active call
   */
  async updateCallVideoInput(callId: string, deviceId?: string): Promise<void> {
    const call = this.calls.get(callId);
    if (!call || !call.hasVideo) {
      throw new Error("No active video call to update");
    }

    try {
      const quality = call.videoQuality || "auto";
      const newStream = await this.videoDeviceService.getVideoStream(
        quality,
        deviceId,
      );
      const newVideoTrack = newStream.getVideoTracks()[0];

      if (!newVideoTrack) {
        throw new Error("Failed to get new video track");
      }

      // Save mute state
      const wasMuted = call.isVideoMuted;

      // Stop old video tracks
      if (call.localStream) {
        call.localStream.getVideoTracks().forEach((track) => {
          call.localStream?.removeTrack(track);
          track.stop();
        });

        // Add new video track
        call.localStream.addTrack(newVideoTrack);
      }

      // Apply mute state
      newVideoTrack.enabled = !wasMuted;

      // Update the sender track in the RTCPeerConnection
      if (call.session.sessionDescriptionHandler) {
        const sdh = call.session.sessionDescriptionHandler as CustomSDH;
        if (sdh.peerConnection) {
          // Find the negotiated video transceiver (one with mid !== null)
          // This is important after video upgrades where multiple transceivers may exist
          const transceivers = sdh.peerConnection.getTransceivers();
          const videoTransceiver = transceivers.find(
            (t) => t.mid !== null && t.receiver?.track?.kind === "video",
          );

          if (videoTransceiver?.sender) {
            await videoTransceiver.sender.replaceTrack(newVideoTrack);
            console.log(
              `Updated video input for call ${callId} via negotiated transceiver`,
            );

            // Reapply bitrate limit after track replacement
            await this.videoQualityService.applyBitrateLimit(
              videoTransceiver.sender,
            );
          } else {
            // Fallback: try finding sender directly (for non-upgrade video calls)
            const videoSender = sdh.peerConnection
              .getSenders()
              .find((sender) => sender.track && sender.track.kind === "video");

            if (videoSender) {
              await videoSender.replaceTrack(newVideoTrack);
              console.log(
                `Updated video input for call ${callId} via direct sender lookup`,
              );

              // Reapply bitrate limit after track replacement
              await this.videoQualityService.applyBitrateLimit(videoSender);
            }
          }
        }
      }

      this.updateActiveCallsList();
    } catch (error) {
      console.error("Error updating call video input:", error);
      throw error;
    }
  }

  /**
   * Get video statistics for a call
   */
  getVideoStats(callId: string): Observable<any> {
    return this.videoQualityService.getVideoStats(callId);
  }

  /**
   * Start video statistics monitoring for a call
   */
  private startVideoStatsMonitoring(
    callId: string,
    peerConnection: RTCPeerConnection,
  ): void {
    this.videoQualityService.startStatsMonitoring(callId, peerConnection);
  }

  /**
   * Stop video statistics monitoring for a call
   */
  private stopVideoStatsMonitoring(callId: string): void {
    this.videoQualityService.stopStatsMonitoring(callId);
  }

  /**
   * Apply bitrate limit to video sender for a session
   */
  private async applyVideoBitrateLimit(session: Session): Promise<void> {
    try {
      if (!session.sessionDescriptionHandler) return;

      const sdh = session.sessionDescriptionHandler as CustomSDH;
      if (!sdh.peerConnection) return;

      const senders = sdh.peerConnection.getSenders();
      const videoSender = senders.find((s) => s.track?.kind === "video");

      if (videoSender) {
        await this.videoQualityService.applyBitrateLimit(videoSender);
        console.log(
          "[SipService] Applied 3 Mbps bitrate limit to video sender",
        );
      }
    } catch (error) {
      console.error("[SipService] Error applying video bitrate limit:", error);
    }
  }

  /**
   * Schedule a delayed stop. Used by widget components on destroy to allow
   * view-switch recreation to cancel the stop before it fires.
   * Real widget removals (no re-initialization within 500ms) will proceed to stop.
   */
  scheduleStop(): void {
    this.cancelPendingStop();
    this._pendingStopTimeout = setTimeout(() => {
      this._pendingStopTimeout = null;
      this.stop();
    }, 500);
  }

  /**
   * Cancel any pending scheduled stop (called by initialize when widget is re-created).
   */
  cancelPendingStop(): void {
    if (this._pendingStopTimeout !== null) {
      clearTimeout(this._pendingStopTimeout);
      this._pendingStopTimeout = null;
    }
  }

  async stop(): Promise<void> {
    // Cancel any pending scheduled stop to prevent double-stop
    this.cancelPendingStop();
    // Set initialized to false IMMEDIATELY to unblock any pending initialize() calls
    this.initialized = false;
    this.isStopping = true;

    const stopWork = (async () => {
      // Stop ringback tone if it's playing
      this.stopRingbackTone();

      // Stop ringing tone if it's playing
      this.toneService.stopRingingTone();

      // Stop call waiting tone if it's playing
      this.toneService.stopCallWaitingTone();

      // end all active calls first
      for (const [callId, call] of this.calls.entries()) {
        try {
          await this.endCall(callId);
        } catch (error) {
          console.error("Error ending call during service stop:", error);
        }
      }

      // clear reconnection interval if it exists
      this.clearReconnectInterval();

      // clear registration retry interval if it exists
      this.clearRegistrationRetryInterval();

      // unregister if registered
      if (this.registerer) {
        try {
          // Mark this as an intentional unregister to prevent auto-retry
          this.isIntentionalUnregister = true;
          await this.registerer.unregister();
          this.registerer = undefined;
          this.isIntentionalUnregister = false;
        } catch (error) {
          console.error("Error unregistering during service stop:", error);
          this.isIntentionalUnregister = false;
        }
      }

      // stop the user agent
      if (this.userAgent) {
        try {
          await this.userAgent.stop();
        } catch (error) {
          console.error("Error stopping user agent:", error);
        }
      }

      // reset remaining service state (initialized already set to false above)
      this.currentConfig = undefined;
      this.calls.clear();
      this.activeCalls.next([]);
      this.activeCallId.next(null);
      this.audioActiveCallId = null;
      this.incomingInvitation = null;
      this._isRegistered.next(false);
      this.isStopping = false;
      this.wsConnectionAttempts = 0;
      this.wsPreAuthAttempted = false;
    })();

    this._stopPromise = stopWork;
    try {
      await stopWork;
    } finally {
      this._stopPromise = null;
    }
  }

  ngOnDestroy() {
    this.cancelPendingStop();
    this.stop();
  }
}
